import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft,
  UserPlus,
  Wrench,
  Folder,
  Star,
  Search,
  List,
  LayoutGrid,
  Plus,
  ChevronLeft,
  Loader2,
  X,
  Check,
  User,
  Users,
  Clock,
  Package,
  Hammer,
  ShoppingCart,
  Mail,
  Phone,
  MapPin,
  Settings,
  Tag,
  Calendar,
  Edit,
  Trash2,
  CreditCard,
  Eye,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Printer,
  ArrowRightLeft,
  Boxes,
  ScanBarcode,
  Layers,
} from "lucide-react";
import { all_routes } from "../../Router/all_routes";
import { API_ENDPOINTS, API_BASE_URL } from "../../api/header";
import apiClient from "../../api/apiClient";
import ReactSelectLib from "react-select";
import { ReactSelect } from "@/components/ui/react-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import CustomerModal from "../../components/modals/CustomerModal";
import InventorySelectorModal from "../sales/components/pos/InventorySelectorModal";
import RecordRepairPaymentModal from "./components/RecordRepairPaymentModal";
import RepairDetailsModal from "./components/RepairDetailsModal";
import PDFModal from "./components/PDFModal";
import AddPartsModal from "./components/AddPartsModal";
import PartsViewModal from "./components/PartsViewModal";
import LaborEntriesModal from "./components/LaborEntriesModal";
import LaborViewModal from "./components/LaborViewModal";
import StatusHistoryModal from "./components/StatusHistoryModal";
import StatusUpdateModal from "./components/StatusUpdateModal";
import PosActionsSidebar from "../pos-common/PosActionsSidebar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select as ShadSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------


const isRepairPaymentPending = (repair) => {
  if (!repair) return false;
  const pStatus = (repair.payment_status || "pending").toLowerCase();
  // Completed, paid, cancelled, or refunded are not pending payment
  if (pStatus === "completed" || pStatus === "paid" || pStatus === "cancelled" || pStatus === "refunded") {
    return false;
  }
  const balanceDue =
    repair.balance_due != null ? parseFloat(repair.balance_due) : null;
  const paidAmount = parseFloat(repair.paid_amount || 0);
  const totalCost = parseFloat(repair.total_cost ?? repair.estimated_cost ?? 0);
  if (balanceDue !== null && balanceDue <= 0 && (paidAmount > 0 || totalCost > 0)) {
    return false;
  }
  const jobStatus = (typeof repair.status === "string" ? repair.status : repair.status?.value || "").toLowerCase();
  if (jobStatus === "cancelled") return false;
  return true;
};

const getRepairBalanceDue = (repair) => {
  if (!repair) return 0;
  const balanceDueVal =
    repair.balance_due != null ? parseFloat(repair.balance_due) : null;
  const totalVal = parseFloat(repair.total_cost ?? repair.estimated_cost ?? 0);
  const paymentStatus = (repair.payment_status || "").toLowerCase();
  const isPaid = paymentStatus === "completed" || paymentStatus === "paid";
  if (balanceDueVal != null) return Math.max(0, balanceDueVal);
  if (isPaid) return 0;
  const paidVal = parseFloat(repair.paid_amount || 0);
  return Math.max(0, parseFloat((totalVal - paidVal).toFixed(2)));
};

const isRepairPaidInFull = (repair) => {
  if (!repair) return false;
  const pStatus = (repair.payment_status || "").toLowerCase();
  if (pStatus === "completed" || pStatus === "paid") {
    return true;
  }
  if (
    pStatus === "pending" ||
    pStatus === "partially-paid" ||
    pStatus === "partially_paid" ||
    pStatus === "partial" ||
    pStatus === "cancelled" ||
    pStatus === "refunded"
  ) {
    return false;
  }
  const totalCost = parseFloat(repair.total_cost ?? repair.estimated_cost ?? 0);
  const paidAmount = parseFloat(repair.paid_amount || 0);
  const balanceDue = getRepairBalanceDue(repair);

  // If there's an actual cost or amount paid, and balance is settled
  return balanceDue <= 0.01 && (paidAmount > 0 || totalCost > 0);
};

const fmtDate = (val) => {
  if (!val) return "-";
  try {
    return new Date(val).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return val;
  }
};

const getStatusBadge = (status) => {
  const statusValue = status?.value || status;
  const statusLabel = status?.label || statusValue;

  const statusConfig = {
    completed: { variant: "success" },
    delivered: { variant: "success" },
    ready: { variant: "success" },
    ready_for_pickup: { variant: "success" },
    in_progress: { variant: "info" },
    diagnosing: { variant: "info" },
    testing: { variant: "info" },
    awaiting_parts: { variant: "warning" },
    on_hold: { variant: "warning" },
    cancelled: { variant: "danger" },
    rejected: { variant: "danger" },
    pending: { variant: "warning" },
    new: { variant: "info" },
  };

  const config = statusConfig[statusValue?.toLowerCase()] || {
    variant: "secondary",
  };

  return (
    <Badge variant={config.variant} className="capitalize text-[10px] px-1.5 py-0 h-4 font-semibold">
      {statusLabel?.replace(/_/g, " ") || "Pending"}
    </Badge>
  );
};

const getPriorityBadge = (priority) => {
  const priorityValue = priority?.value || priority;
  const priorityLabel = priority?.label || priorityValue;

  const priorityConfig = {
    urgent: { variant: "destructive" },
    critical: { variant: "destructive" },
    high: { variant: "default" },
    medium: { variant: "secondary" },
    normal: { variant: "outline" },
    low: { variant: "secondary" },
  };

  const config = priorityConfig[String(priorityValue || "").toLowerCase()] || {
    variant: "secondary",
  };

  return (
    <Badge variant={config.variant} className="capitalize text-[10px] px-1.5 py-0 h-4 font-semibold">
      {priorityLabel || "Normal"}
    </Badge>
  );
};

const RepairCardSkeleton = ({ count = 6 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-3">
    {[...Array(count)].map((_, i) => (
      <div key={i} className="flex flex-col space-y-2">
        <Skeleton className="h-[110px] w-full rounded-xl" />
      </div>
    ))}
  </div>
);

const CategoryCardSkeleton = ({ count = 10 }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2 xl:gap-4 pb-4">
    {[...Array(count)].map((_, i) => (
      <Skeleton key={i} className="aspect-square w-full rounded-xl" />
    ))}
  </div>
);

// -----------------------------------------------------------------------------
// Predefined Repair Card
// -----------------------------------------------------------------------------
const PredefinedRepairCard = ({
  repair,
  isSelected,
  onSelect,
  currencySymbol,
}) => {
  const partsCount = repair.parts_n_components?.length || 0;
  const laborCount = repair.labor_n_timetracking?.length || 0;

  return (
    <Card
      onClick={() => onSelect(repair)}
      className={`cursor-pointer transition-all hover:shadow-md border-border overflow-hidden group ${isSelected
        ? "border-primary ring-2 ring-primary/30 bg-primary/5"
        : "hover:border-primary/50"
        }`}
    >
      <CardContent className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h6
            className="font-semibold text-sm leading-tight line-clamp-2 group-hover:text-primary transition-colors flex-1"
            title={repair.name}
          >
            {repair.name}
          </h6>
          {(() => {
            const pVal = String(repair.priority?.value || repair.priority || "").toLowerCase();
            if (!pVal || pVal === "normal") return null;
            return <div className="shrink-0">{getPriorityBadge(repair.priority)}</div>;
          })()}
        </div>

        {repair.item_description && (
          <p className="text-[11px] text-muted-foreground line-clamp-1">
            {repair.item_description}
          </p>
        )}

        <div className="flex items-center justify-between pt-1">
          <span className="font-bold text-sm text-primary">
            {currencySymbol}
            {parseFloat(repair.estimated_cost || 0).toFixed(2)}
          </span>
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
            {partsCount > 0 && (
              <span className="flex items-center gap-0.5">
                <Package size={10} />
                {partsCount}
              </span>
            )}
            {laborCount > 0 && (
              <span className="flex items-center gap-0.5">
                <Clock size={10} />
                {laborCount}
              </span>
            )}
          </div>
        </div>

        {isSelected && (
          <div className="flex items-center gap-1 text-[10px] text-primary font-medium">
            <Check size={10} />
            Selected
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// -----------------------------------------------------------------------------
// Category Card
// -----------------------------------------------------------------------------
const RepairCategoryCard = ({ category, onSelect }) => (
  <Card
    onClick={() => onSelect(category.id)}
    className="relative cursor-pointer transition-all duration-200 hover:shadow-md border-border overflow-hidden hover:border-primary/50 group w-full aspect-square flex flex-col items-center justify-center select-none"
  >
    {/* Featured Corner Label */}
    {Boolean(category.is_featured) && (
      <div className="absolute top-0 right-0 z-10">
        <div className="bg-warning text-white text-[10px] font-bold px-2 py-0.5 rounded-bl-lg shadow-xs flex items-center gap-1 leading-none tracking-wide">
          <Star size={9} fill="currentColor" />
          <span>Featured</span>
        </div>
      </div>
    )}

    <CardContent className="p-2.5 sm:p-3 flex flex-col items-center justify-center text-center h-full w-full gap-1.5 sm:gap-2">
      {/* Image / Icon Section */}
      <div className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 bg-secondary/10 rounded-xl overflow-hidden border border-secondary/20 flex items-center justify-center transition-transform duration-200 group-hover:scale-105">
        {category.image ? (
          <img
            src={category.image}
            alt={category.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <Wrench
            size={22}
            className="text-muted-foreground/60 group-hover:text-primary transition-colors"
          />
        )}
      </div>

      {/* Category Details (down by down) */}
      <div className="w-full px-1 flex flex-col items-center justify-center min-w-0">
        <h6
          className="font-semibold text-xs sm:text-sm leading-snug line-clamp-2 sm:line-clamp-3 text-foreground group-hover:text-primary transition-colors break-words text-center"
          title={category.name}
        >
          {category.name}
        </h6>
      </div>
    </CardContent>
  </Card>
);

// -----------------------------------------------------------------------------
// Main Component
// -----------------------------------------------------------------------------

const RepairPOS = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const predefinedParamId = searchParams.get("predefined");
  const existingParamId =
    searchParams.get("existing_id") || searchParams.get("repair_id");

  const currentUser = useSelector((state) => state.user_data);
  const currentStore = useSelector((state) => state.current_store);
  const bearer = useSelector((state) => state.bearer_token);

  const permissions = useSelector((state) => state.userpermission_data);

  const hasPermission = React.useCallback(
    (perm) => {
      const perms = permissions || [];
      return Boolean(perms.includes(perm));
    },
    [permissions],
  );
  const currency = useSelector(
    (state) => state.current_store?.default_currency,
  );
  const currencySymbol = currency?.symbol || "";
  const queryClient = useQueryClient();

  // -- Modals & Actions for Regular Repairs, Status, Parts & Labor -----------
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentRepair, setPaymentRepair] = useState(null);

  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsRepair, setDetailsRepair] = useState(null);

  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfRepairData, setPdfRepairData] = useState(null);

  const [statusUpdateOpen, setStatusUpdateOpen] = useState(false);
  const [statusUpdateRepair, setStatusUpdateRepair] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: "", notes: "" });

  const [statusHistoryOpen, setStatusHistoryOpen] = useState(false);
  const [statusHistoryRepair, setStatusHistoryRepair] = useState(null);

  const [addPartsOpen, setAddPartsOpen] = useState(false);
  const [addPartsRepair, setAddPartsRepair] = useState(null);
  const [partsFormData, setPartsFormData] = useState({
    product_id: "",
    quantity_used: 1,
    unit_cost: 0,
    notes: "",
    serial_numbers: [],
    inventory_batch_id: null,
  });
  const [partsViewOpen, setPartsViewOpen] = useState(false);
  const [partsViewRepair, setPartsViewRepair] = useState(null);
  const [partsProductSearchTerm] = useState("");

  const [laborModalOpen, setLaborModalOpen] = useState(false);
  const [laborViewOpen, setLaborViewOpen] = useState(false);
  const [laborRepair, setLaborRepair] = useState(null);
  const [newLaborEntry, setNewLaborEntry] = useState({
    technician_id: "",
    hourly_rate: "",
    start_time: "",
    end_time: "",
    description: "",
  });

  const [mobileView, setMobileView] = useState("form"); // "form" | "catalog"

  // -- Customer --------------------------------------------------------------
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerSearchExpanded, setCustomerSearchExpanded] = useState(false);
  const customerSearchTimeoutRef = useRef(null);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);

  // -- Repair selection ------------------------------------------------------
  const [selectedRepair, setSelectedRepair] = useState(null);
  const [selectedExistingRepair, setSelectedExistingRepair] = useState(null);
  const [isFromScratch, setIsFromScratch] = useState(false);
  const [currentCat, setCurrentCat] = useState(null);
  const [activeTab, setActiveTab] = useState("by-category");

  // -- Override / form fields -------------------------------------------------
  const [itemDescription, setItemDescription] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [devicePassword, setDevicePassword] = useState("");
  const [priority, setPriority] = useState("normal");
  const [estimatedCost, setEstimatedCost] = useState("");
  const [issueDescription, setIssueDescription] = useState("");

  const [brandSearchInput, setBrandSearchInput] = useState("");
  const [brandSearchTerm, setBrandSearchTerm] = useState("");
  const [modelSearchInput, setModelSearchInput] = useState("");
  const [modelSearchTerm, setModelSearchTerm] = useState("");
  const [supplierSearchInput, setSupplierSearchInput] = useState("");
  const [supplierSearchTerm, setSupplierSearchTerm] = useState("");

  const [brandLabels, setBrandLabels] = useState({});
  const [modelLabels, setModelLabels] = useState({});
  const [supplierLabels, setSupplierLabels] = useState({});

  useEffect(() => {
    const timer = setTimeout(() => setBrandSearchTerm(brandSearchInput), 300);
    return () => clearTimeout(timer);
  }, [brandSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => setModelSearchTerm(modelSearchInput), 300);
    return () => clearTimeout(timer);
  }, [modelSearchInput]);

  useEffect(() => {
    const timer = setTimeout(
      () => setSupplierSearchTerm(supplierSearchInput),
      300,
    );
    return () => clearTimeout(timer);
  }, [supplierSearchInput]);
  const [diagnosis, setDiagnosis] = useState("");
  const [estimatedCompletionDate, setEstimatedCompletionDate] = useState("");
  const [technicianId, setTechnicianId] = useState("");
  const [repairCategoryIds, setRepairCategoryIds] = useState([]);
  const [customerNotes, setCustomerNotes] = useState("");
  const [technicianNotes, setTechnicianNotes] = useState("");

  // -- Parts, Labor & Special Parts ------------------------------------------
  const [selectedParts, setSelectedParts] = useState([]);
  const [laborEntries, setLaborEntries] = useState([]);
  const [specialParts, setSpecialParts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [inventorySelectorOpen, setInventorySelectorOpen] = useState(false);
  const [inventorySelectorTarget, setInventorySelectorTarget] = useState(null);
  const [specialPartModalOpen, setSpecialPartModalOpen] = useState(false);
  const [newSpecialPart, setNewSpecialPart] = useState({
    item_name: "",
    brand_id: "",
    model_id: "",
    required_quantity: 1,
    unit_cost: "",
    retail_price: "",
    supplier_id: "",
    order_link: "",
    tracking_id: "",
    order_notes: "",
    order_date: "",
    status: "pending",
    is_tax_exclusive: false,
    has_purchase_order: false,
    purchase_order_id: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------
  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ["repair-categories"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.REPAIRS.CATEGORIES);
      return res.data.data || [];
    },
  });

  // All repairs (for "All Repairs" tab) - fetches first 100 active only
  const { data: predefinedRepairs = [], isLoading: repairsLoading } = useQuery({
    queryKey: ["predefined-repairs-pos", "all"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.REPAIRS.PREDEFINED_JOBS, {
        params: {
          per_page: 100,
          with: ["repairCategories"],
        },
      });
      return res.data.data || [];
    },
  });

  // Category-filtered repairs - re-fetches from server when category changes
  const { data: categoryRepairs = [], isLoading: categoryRepairsLoading } =
    useQuery({
      queryKey: ["predefined-repairs-pos", "category", currentCat],
      queryFn: async () => {
        const res = await apiClient.get(API_ENDPOINTS.REPAIRS.PREDEFINED_JOBS, {
          params: {
            per_page: 100,
            repair_category_ids: [currentCat],
            with: ["repairCategories"],
          },
        });
        return res.data.data || [];
      },
      enabled: !!currentCat,
    });

  const { data: customers = [], isFetching: customersLoading } = useQuery({
    queryKey: ["customers-repair-pos", customerSearch],
    queryFn: async () => {
      if (!customerSearch || customerSearch.length < 2) return [];
      const res = await apiClient.get(API_ENDPOINTS.SALES.CUSTOMERS, {
        params: { q: customerSearch },
      });
      return res.data.data || [];
    },
    enabled: customerSearch.length >= 2,
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.USERS.EMPLOYEES);
      return (res.data.data || []).filter((e) => e.status === "active");
    },
  });

  const { data: posProducts = [], isFetching: productsLoading } = useQuery({
    queryKey: ["pos-products-repair-pos", productSearch, currentStore?.id],
    queryFn: async () => {
      const params = {
        perPage: 25,
        search: productSearch || undefined,
        sellable: true,
        with: [
          "brand",
          "model",
          "unit",
          "categories",
          "serial_numbers",
          "batches",
        ],
      };
      if (currentStore?.id) params.store_id = currentStore.id;
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.POS_PRODUCTS, {
        params,
      });
      return res.data.data || [];
    },
  });

  // Fetch product details for template parts so names resolve correctly
  const templatePartIds = useMemo(
    () =>
      (selectedRepair?.parts_n_components || [])
        .map((p) => p.part_id)
        .filter(Boolean),
    [selectedRepair],
  );
  const { data: templatePartProducts = [] } = useQuery({
    queryKey: ["template-part-products", templatePartIds.join(",")],
    queryFn: async () => {
      const results = await Promise.all(
        templatePartIds.map((id) =>
          apiClient
            .get(API_ENDPOINTS.INVENTORY.POS_PRODUCT_DETAILS(id))
            .then((r) => r.data.data)
            .catch(() => null),
        ),
      );
      return results.filter(Boolean);
    },
    enabled: templatePartIds.length > 0,
  });

  const { data: suppliers = [], isFetching: isSuppliersFetching } = useQuery({
    queryKey: ["suppliers", supplierSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.PURCHASES.SUPPLIERS, {
        params: { search: supplierSearchTerm || undefined },
      });
      return res.data.data || [];
    },
    enabled: specialPartModalOpen,
  });

  const { data: brands = [], isFetching: isBrandsFetching } = useQuery({
    queryKey: ["brands", brandSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.BRANDS, {
        params: { search: brandSearchTerm || undefined },
      });
      return res.data.data || [];
    },
    enabled: specialPartModalOpen && hasPermission("brand read"),
  });

  const { data: brandModels = [], isFetching: isModelsFetching } = useQuery({
    queryKey: [
      "brand-models-repair-pos",
      newSpecialPart.brand_id,
      modelSearchTerm,
    ],
    queryFn: async () => {
      const res = await apiClient.get(
        API_ENDPOINTS.INVENTORY.BRAND_MODELS(newSpecialPart.brand_id),
        {
          params: { search: modelSearchTerm || undefined },
        },
      );
      return res.data.data || [];
    },
    enabled:
      specialPartModalOpen &&
      !!newSpecialPart.brand_id &&
      hasPermission("brand model read"),
  });

  useEffect(() => {
    if (brands.length > 0) {
      setBrandLabels((prev) => {
        const next = { ...prev };
        brands.forEach((b) => {
          next[String(b.id)] = b.name;
        });
        return next;
      });
    }
  }, [brands]);

  useEffect(() => {
    if (brandModels.length > 0) {
      setModelLabels((prev) => {
        const next = { ...prev };
        brandModels.forEach((m) => {
          next[String(m.id)] = m.name;
        });
        return next;
      });
    }
  }, [brandModels]);

  useEffect(() => {
    if (suppliers.length > 0) {
      setSupplierLabels((prev) => {
        const next = { ...prev };
        suppliers.forEach((s) => {
          next[String(s.id)] = s.name;
        });
        return next;
      });
    }
  }, [suppliers]);

  const { data: availableBatches = [] } = useQuery({
    queryKey: [
      "repair-pos-batches",
      inventorySelectorTarget?.productId,
      currentStore?.id,
    ],
    queryFn: async () => {
      const res = await apiClient.get(
        API_ENDPOINTS.INVENTORY.BATCHES_AVAILABLE(
          inventorySelectorTarget.productId,
          currentStore.id,
        ),
      );
      return res.data.data || [];
    },
    enabled:
      inventorySelectorOpen && inventorySelectorTarget?.type === "standard",
  });

  const { data: availableSerials = [] } = useQuery({
    queryKey: [
      "repair-pos-serials",
      inventorySelectorTarget?.productId,
      currentStore?.id,
    ],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.SERIALS_AVAILABLE, {
        params: {
          product_id: inventorySelectorTarget.productId,
          store_id: currentStore?.id,
        },
      });
      return res.data.data || [];
    },
    enabled:
      inventorySelectorOpen && inventorySelectorTarget?.type === "serial",
  });

  // POS init - provides walk_in_customer default
  const { data: posInitData } = useQuery({
    queryKey: ["pos-init"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.SALES.POS_INIT);
      return res.data;
    },
    staleTime: Infinity,
  });

  // Fetch the predefined repair specified in ?predefined= query param (if any)
  const { data: preselectedRepair } = useQuery({
    queryKey: ["predefined-repair-preselect", predefinedParamId],
    queryFn: async () => {
      const res = await apiClient.get(
        API_ENDPOINTS.REPAIRS.PREDEFINED_JOB_DETAILS(predefinedParamId),
      );
      return res.data.data || null;
    },
    enabled: !!predefinedParamId && !!bearer,
    staleTime: Infinity,
  });

  // Fetch the existing repair specified in ?existing_id= query param (if any)
  const { data: routeExistingRepair } = useQuery({
    queryKey: ["repair-job-existing-param", existingParamId],
    queryFn: async () => {
      const res = await apiClient.get(
        API_ENDPOINTS.REPAIRS.REPAIR_DETAILS(existingParamId),
      );
      return res.data?.data || res.data || null;
    },
    enabled: !!existingParamId && !!bearer,
  });

  const canReadRepairJobs =
    hasPermission("repair job read") || hasPermission("repair job add_payment");

  // Regular repair jobs query (for Repair Jobs in POS)
  const {
    data: regularRepairsResponse,
    refetch: refetchRegularRepairs,
  } = useQuery({
    queryKey: [
      "repair-jobs",
      "pos-pending",
      currentStore?.id,
    ],
    queryFn: async () => {
      const params = {
        per_page: 50,
        sort_by: "created_at",
        sort_direction: "desc",
        with: [
          "customer",
          "repairCategories",
          "technician",
          "store",
          "parts",
          "parts.product",
          "statusHistory",
          "statusHistory.changedBy",
          "laborEntries",
          "laborEntries.technician",
        ],
      };
      if (currentStore?.id) params.store_id = currentStore.id;

      const res = await apiClient.get(API_ENDPOINTS.REPAIRS.JOBS, {
        params,
      });
      return res.data;
    },
    enabled: Boolean(canReadRepairJobs),
  });

  const repairJobs = useMemo(() => {
    if (!regularRepairsResponse) return [];
    if (Array.isArray(regularRepairsResponse.data)) {
      return regularRepairsResponse.data;
    }
    if (Array.isArray(regularRepairsResponse)) {
      return regularRepairsResponse;
    }
    return [];
  }, [regularRepairsResponse]);

  // All unpaid regular repair jobs identified by payment_status
  const pendingPaymentRepairs = useMemo(() => {
    return repairJobs.filter((repair) => isRepairPaymentPending(repair));
  }, [repairJobs]);

  const handleGeneratePDFReport = async (id, type = "invoice") => {
    try {
      setPdfGenerating(true);
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
        setPdfBlobUrl(null);
      }
      const endpoint =
        type === "job-note"
          ? "job-note"
          : type === "job-label"
            ? "job-label"
            : "invoice";

      const response = await apiClient.get(
        `${API_BASE_URL}/reports/pdf/repairs/${id}/${endpoint}`,
        { responseType: "blob" },
      );

      const blobUrl = URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      setPdfBlobUrl(blobUrl);
    } catch (error) {
      console.error("PDF Report error:", error);
      toast.error("Error", {
        description:
          error.response?.data?.message || "Failed to generate PDF report",
      });
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleClosePDFModal = () => {
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setPdfGenerating(false);
    setPdfModalOpen(false);
    setPdfRepairData(null);
  };

  // Set walk-in customer as default once init data loads
  useEffect(() => {
    if (posInitData?.walk_in_customer && !customer) {
      setCustomer(posInitData.walk_in_customer);
      setSelectedCustomerId(posInitData.walk_in_customer.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posInitData]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (customerSearchTimeoutRef.current) {
        clearTimeout(customerSearchTimeoutRef.current);
      }
    };
  }, []);

  // -------------------------------------------------------------------------
  // Derived data
  // -------------------------------------------------------------------------
  const totals = useMemo(() => {
    const source = selectedRepair;
    if (!source) return { parts: 0, labor: 0, total: 0 };

    // Use pre-computed totals from API when available
    const parts =
      source.total_parts_cost != null
        ? parseFloat(source.total_parts_cost)
        : (source.parts_n_components || []).reduce(
          (sum, p) =>
            sum + parseFloat(p.cost || 0) * parseFloat(p.quantity || 1),
          0,
        );

    const labor =
      source.total_labor_cost != null ? parseFloat(source.total_labor_cost) : 0; // Templates store hourly_rate only, not hours - use estimated_cost for total

    const total = parts + labor || parseFloat(source.estimated_cost || 0);
    return { parts, labor, total };
  }, [selectedRepair]);

  const scratchTotals = useMemo(() => {
    const parts = selectedParts.reduce(
      (sum, p) =>
        sum + parseFloat(p.quantity_used || 0) * parseFloat(p.unit_cost || 0),
      0,
    );
    const labor = laborEntries.reduce((sum, l) => {
      if (!l.start_time || !l.end_time || !l.hourly_rate) return sum;
      const hours = Math.max(
        0,
        (new Date(l.end_time) - new Date(l.start_time)) / 3600000,
      );
      return sum + hours * parseFloat(l.hourly_rate || 0);
    }, 0);
    return { parts, labor };
  }, [selectedParts, laborEntries]);

  // -- Products for Add Parts Modal ------------------------------------------
  const { data: addPartsProducts = [], isFetching: isAddPartsProductsLoading } =
    useQuery({
      queryKey: [
        "pos-products-repair-parts",
        partsProductSearchTerm,
        currentStore?.id,
      ],
      queryFn: async () => {
        const params = {
          per_page: 50,
          search: partsProductSearchTerm || undefined,
          with: [
            "brand",
            "model",
            "unit",
            "categories",
            "serial_numbers",
            "batches",
          ],
        };
        if (currentStore?.id) params.store_id = currentStore.id;
        const res = await apiClient.get(API_ENDPOINTS.INVENTORY.POS_PRODUCTS, {
          params,
        });
        return res.data.data || [];
      },
      enabled: Boolean(addPartsOpen || partsViewOpen),
    });

  const combinedRepairJobs = useMemo(() => {
    if (!selectedExistingRepair) return repairJobs;
    if (
      repairJobs.some(
        (j) => String(j.id) === String(selectedExistingRepair.id),
      )
    ) {
      return repairJobs;
    }
    return [selectedExistingRepair, ...repairJobs];
  }, [repairJobs, selectedExistingRepair]);

  const refreshSelectedExistingRepair = useCallback(async (repairId) => {
    if (!repairId) return;
    try {
      const detailRes = await apiClient.get(
        API_ENDPOINTS.REPAIRS.REPAIR_DETAILS(repairId),
      );
      const freshData = detailRes.data?.data || detailRes.data;
      if (freshData) {
        setSelectedExistingRepair(freshData);
      }
    } catch (err) {
      console.warn("Could not refresh repair details:", err);
    }
  }, []);

  // -------------------------------------------------------------------------
  // Mutations
  // -------------------------------------------------------------------------
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status, notes }) =>
      apiClient.post(API_ENDPOINTS.REPAIRS.STATUS_UPDATE(id), {
        status,
        notes: notes || null,
      }),
    onSuccess: async (_, variables) => {
      toast.success("Status updated successfully");
      queryClient.invalidateQueries({ queryKey: ["repair-jobs"] });
      refetchRegularRepairs();
      if (
        selectedExistingRepair &&
        String(selectedExistingRepair.id) === String(variables.id)
      ) {
        await refreshSelectedExistingRepair(variables.id);
      }
      setStatusUpdateOpen(false);
      setStatusUpdateRepair(null);
      setStatusForm({ status: "", notes: "" });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to update repair status",
      );
    },
  });

  const addPartsMutation = useMutation({
    mutationFn: ({ id, data }) =>
      apiClient.post(API_ENDPOINTS.REPAIRS.PARTS(id), data),
    onSuccess: async (_, variables) => {
      toast.success("Part added successfully");
      queryClient.invalidateQueries({ queryKey: ["repair-jobs"] });
      refetchRegularRepairs();
      if (
        selectedExistingRepair &&
        String(selectedExistingRepair.id) === String(variables.id)
      ) {
        await refreshSelectedExistingRepair(variables.id);
      }
      setAddPartsOpen(false);
      setAddPartsRepair(null);
      setPartsFormData({
        product_id: "",
        quantity_used: 1,
        unit_cost: 0,
        notes: "",
        serial_numbers: [],
        inventory_batch_id: null,
      });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to add part");
    },
  });

  const addLaborMutation = useMutation({
    mutationFn: ({ id, data }) =>
      apiClient.post(API_ENDPOINTS.REPAIRS.LABOR(id), data),
    onSuccess: async (_, variables) => {
      toast.success("Labor entry added successfully");
      queryClient.invalidateQueries({ queryKey: ["repair-jobs"] });
      refetchRegularRepairs();
      if (
        selectedExistingRepair &&
        String(selectedExistingRepair.id) === String(variables.id)
      ) {
        await refreshSelectedExistingRepair(variables.id);
      }
      setLaborModalOpen(false);
      setLaborRepair(null);
      setNewLaborEntry({
        technician_id: "",
        hourly_rate: "",
        start_time: "",
        end_time: "",
        description: "",
      });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to add labor entry",
      );
    },
  });

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------
  const handleOpenStatusUpdate = (repair) => {
    const rep = repair || selectedExistingRepair;
    if (!rep) return;
    setStatusUpdateRepair(rep);
    setStatusForm({
      status:
        typeof rep.status === "string"
          ? rep.status
          : rep.status?.value || "",
      notes: "",
    });
    setStatusUpdateOpen(true);
  };

  const handleOpenStatusHistory = (repair) => {
    const rep = repair || selectedExistingRepair;
    if (!rep) return;
    setStatusHistoryRepair(rep);
    setStatusHistoryOpen(true);
  };

  const handleProductSelectForParts = async (e) => {
    const productId = e.target.value;
    const product = addPartsProducts.find((p) => String(p.id) === String(productId));
    const trackingType =
      product?.tracking_type ||
      (product?.is_serialized ? "serial" : product?.has_batches ? "standard" : "none");

    setPartsFormData((f) => ({
      ...f,
      product_id: productId,
      unit_cost: parseFloat(product?.sell_price || 0),
      tracking_type: trackingType,
      inventory_batch_id: null,
      inventory_batch_number: "",
      serial_numbers: [],
    }));
    if (productId) {
      try {
        const res = await apiClient.get(
          API_ENDPOINTS.INVENTORY.POS_PRODUCT_DETAILS(productId),
        );
        const details = res.data.data;
        if (details) {
          const updatedTracking =
            details.tracking_type ||
            (details.is_serialized
              ? "serial"
              : details.has_batches
                ? "standard"
                : trackingType);
          setPartsFormData((f) => ({
            ...f,
            unit_cost: parseFloat(details.sell_price || f.unit_cost || 0),
            tracking_type: updatedTracking,
          }));
        }
      } catch {
        // Handle error gracefully if product fetch fails
      }
    }
  };

  const handleSerialNumbersChangeForParts = (value) => {
    const serials = value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    setPartsFormData((f) => ({ ...f, serial_numbers: serials }));
  };

  const handleSavePartsModal = () => {
    if (!partsFormData.product_id) {
      toast.error("Please select a product");
      return;
    }
    const qty = parseFloat(partsFormData.quantity_used);
    if (!qty || qty <= 0) {
      toast.error("Quantity must be greater than 0");
      return;
    }
    const product = addPartsProducts.find(
      (p) => String(p.id) === String(partsFormData.product_id),
    );
    const trackingType =
      partsFormData.tracking_type ||
      product?.tracking_type ||
      (product?.is_serialized
        ? "serial"
        : product?.has_batches
          ? "standard"
          : "none");

    if (trackingType === "standard" && !partsFormData.inventory_batch_id) {
      toast.error("Please select an inventory batch for this product");
      return;
    }
    if (trackingType === "serial") {
      const serialsCount = (partsFormData.serial_numbers || []).length;
      if (serialsCount !== qty) {
        toast.error(
          `Please select exactly ${qty} serial number${qty > 1 ? "s" : ""} (currently selected: ${serialsCount})`,
        );
        return;
      }
    }

    addPartsMutation.mutate({
      id: addPartsRepair.id,
      data: {
        product_id: partsFormData.product_id,
        inventory_batch_id: partsFormData.inventory_batch_id || null,
        quantity_used: qty,
        unit_cost: parseFloat(partsFormData.unit_cost) || 0,
        notes: partsFormData.notes || null,
        serial_numbers:
          trackingType === "serial" && partsFormData.serial_numbers?.length > 0
            ? partsFormData.serial_numbers
            : null,
      },
    });
  };

  const handleOpenAddParts = (repair) => {
    const rep = repair || selectedExistingRepair;
    if (!rep) return;
    setAddPartsRepair(rep);
    setPartsFormData({
      product_id: "",
      quantity_used: 1,
      unit_cost: 0,
      notes: "",
      serial_numbers: [],
      inventory_batch_id: null,
      inventory_batch_number: "",
      tracking_type: "none",
    });
    setAddPartsOpen(true);
  };

  const handleOpenViewParts = (repair) => {
    const rep = repair || selectedExistingRepair;
    if (!rep) return;
    setPartsViewRepair(rep);
    setPartsViewOpen(true);
  };

  const handleOpenAddLabor = (repair) => {
    const rep = repair || selectedExistingRepair;
    if (!rep) return;
    setLaborRepair(rep);
    setNewLaborEntry({
      technician_id: "",
      hourly_rate: "",
      start_time: "",
      end_time: "",
      description: "",
    });
    setLaborModalOpen(true);
  };

  const handleOpenViewLabor = (repair) => {
    const rep = repair || selectedExistingRepair;
    if (!rep) return;
    setLaborRepair(rep);
    setLaborViewOpen(true);
  };

  const handleLaborEntryChange = (e) => {
    const { name, value } = e.target;
    if (name === "technician_id") {
      const tech = employees.find((t) => t.id.toString() === value);
      setNewLaborEntry((prev) => ({
        ...prev,
        technician_id: value,
        hourly_rate: tech?.hourly_rate ?? prev.hourly_rate,
      }));
    } else {
      setNewLaborEntry((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleLaborEntrySave = () => {
    if (
      !newLaborEntry.technician_id ||
      !newLaborEntry.start_time ||
      !newLaborEntry.hourly_rate
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    const startDate = new Date(newLaborEntry.start_time);
    if (isNaN(startDate.getTime())) {
      toast.error("Invalid start time");
      return;
    }

    let endDate = null;
    if (newLaborEntry.end_time) {
      endDate = new Date(newLaborEntry.end_time);
      if (isNaN(endDate.getTime())) {
        toast.error("Invalid end time");
        return;
      }
      if (endDate <= startDate) {
        toast.error("End time must be after start time");
        return;
      }
    }

    addLaborMutation.mutate({
      id: laborRepair.id,
      data: {
        technician_id: newLaborEntry.technician_id,
        start_time: startDate.toISOString(),
        end_time: endDate ? endDate.toISOString() : null,
        hourly_rate: parseFloat(newLaborEntry.hourly_rate),
        description: newLaborEntry.description || null,
      },
    });
  };
  const handleSelectCustomer = (opt) => {
    if (!opt) return;
    setSelectedCustomerId(opt.value);
    setCustomer(opt.customerData || { id: opt.value, full_name: opt.label });
  };

  const handleSelectRepair = (repair) => {
    setSelectedRepair(repair);
    setSelectedExistingRepair(null);
    setIsFromScratch(false);
    // Pre-fill overrides from template
    setItemDescription(repair.item_description || "");
    setIssueDescription(repair.issue_description || "");
    setDiagnosis(repair.diagnosis || "");
    setPriority(repair.priority?.value || repair.priority || "normal");
    setEstimatedCost(
      repair.estimated_cost ? String(repair.estimated_cost) : "",
    );
    setEstimatedCompletionDate(repair.estimated_completion_date || "");
    // repair_category_ids may come as flat int array or only as repair_categories objects
    const catIds =
      Array.isArray(repair.repair_category_ids) &&
        repair.repair_category_ids.length > 0
        ? repair.repair_category_ids.map((id) =>
          typeof id === "string" ? parseInt(id, 10) : id,
        )
        : (repair.repair_categories || []).map((c) => c.id);
    setRepairCategoryIds(catIds);
    setCustomerNotes(repair.customer_notes || "");
    setTechnicianNotes(repair.technician_notes || "");
    setSerialNumber("");
    setDevicePassword("");
    setTechnicianId(
      currentUser?.employee_id ? String(currentUser.employee_id) : "",
    );
    setSelectedParts([]);
    // Pre-fill labor from template - labor_id is the technician UUID
    setLaborEntries(
      (repair.labor_n_timetracking || []).map((l) => ({
        technician_id: l.labor_id || "",
        hourly_rate: String(l.hourly_rate || ""),
        start_time: "",
        end_time: "",
        description: l.description || "",
      })),
    );
    setSpecialParts([]);
  };

  const handleSelectExistingRepair = async (repair) => {
    setSelectedExistingRepair(repair);
    setSelectedRepair(null);
    setIsFromScratch(false);
    if (repair.customer) {
      setSelectedCustomerId(repair.customer.id);
      setCustomer(repair.customer);
    } else if (repair.customer_id) {
      setSelectedCustomerId(repair.customer_id);
      if (
        posInitData?.walk_in_customer &&
        (repair.customer_id === posInitData.walk_in_customer.id ||
          repair.customer_id === "00000000-0000-0000-0000-000000000001")
      ) {
        setCustomer(posInitData.walk_in_customer);
      }
    }
    setMobileView("form");

    // Fetch full details to ensure parts, labor entries and status history are populated
    if (repair?.id) {
      try {
        const detailRes = await apiClient.get(
          API_ENDPOINTS.REPAIRS.REPAIR_DETAILS(repair.id),
        );
        const fresh = detailRes.data?.data || detailRes.data;
        if (fresh) {
          setSelectedExistingRepair(fresh);
          if (fresh.customer) {
            setSelectedCustomerId(fresh.customer.id);
            setCustomer(fresh.customer);
          }
        }
      } catch (err) {
        console.warn("Could not fetch full details on repair selection:", err);
      }
    }
  };

  // Keep selectedExistingRepair synchronized when regular repairs data updates
  useEffect(() => {
    if (selectedExistingRepair?.id && repairJobs.length > 0) {
      const updated = repairJobs.find(
        (r) => String(r.id) === String(selectedExistingRepair.id),
      );
      if (
        updated &&
        (updated.paid_amount !== selectedExistingRepair.paid_amount ||
          updated.balance_due !== selectedExistingRepair.balance_due ||
          updated.payment_status !== selectedExistingRepair.payment_status ||
          updated.status !== selectedExistingRepair.status ||
          (updated.parts && updated.parts.length !== (selectedExistingRepair.parts || []).length) ||
          ((updated.labor_entries || updated.laborEntries) &&
            (updated.labor_entries || updated.laborEntries).length !==
            (selectedExistingRepair.labor_entries || selectedExistingRepair.laborEntries || []).length))
      ) {
        setSelectedExistingRepair((prev) => ({
          ...prev,
          ...updated,
          parts: updated.parts || prev?.parts || [],
          labor_entries:
            updated.labor_entries ||
            updated.laborEntries ||
            prev?.labor_entries ||
            prev?.laborEntries ||
            [],
        }));
      }
    }
  }, [repairJobs, selectedExistingRepair]);

  // Auto-select predefined repair from ?predefined= query param
  useEffect(() => {
    if (preselectedRepair && !selectedRepair && !isFromScratch && !selectedExistingRepair) {
      handleSelectRepair(preselectedRepair);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedRepair]);

  // Auto-select existing repair from ?existing_id= query param
  useEffect(() => {
    if (routeExistingRepair && !selectedExistingRepair) {
      handleSelectExistingRepair(routeExistingRepair);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeExistingRepair]);

  const handleFromScratch = () => {
    setSelectedRepair(null);
    setSelectedExistingRepair(null);
    setIsFromScratch(true);
    setItemDescription("");
    setIssueDescription("");
    setDiagnosis("");
    setPriority("normal");
    setEstimatedCost("");
    setEstimatedCompletionDate("");
    setRepairCategoryIds([]);
    setCustomerNotes("");
    setTechnicianNotes("");
    setSerialNumber("");
    setDevicePassword("");
    setTechnicianId(
      currentUser?.employee_id ? String(currentUser.employee_id) : "",
    );
    setSelectedParts([]);
    setLaborEntries([]);
    setSpecialParts([]);
  };

  const handleClearSelection = () => {
    setSelectedRepair(null);
    setSelectedExistingRepair(null);
    setIsFromScratch(false);
  };

  const handleCreateRepair = async () => {
    if (!selectedCustomerId) {
      toast.error("Please select a customer");
      return;
    }
    if (!selectedRepair && !isFromScratch) {
      toast.error("Please select a predefined repair or create from scratch");
      return;
    }
    if (!itemDescription.trim()) {
      toast.error("Item description is required");
      return;
    }
    if (!issueDescription.trim()) {
      toast.error("Reported issue is required");
      return;
    }
    if (repairCategoryIds.length === 0) {
      toast.error("At least one repair category is required");
      return;
    }

    // Validate repair parts batch and serial tracking
    for (let i = 0; i < selectedParts.length; i++) {
      const part = selectedParts[i];
      const trackingType = part.tracking_type;
      if (trackingType === "standard" && !part.inventory_batch_id) {
        toast.error(
          `Part #${i + 1} (${part.product_name || "Part"}): Please select an inventory batch`,
        );
        return;
      }
      if (trackingType === "serial") {
        const qty = parseFloat(part.quantity_used) || 1;
        const serialsCount = (part.serial_numbers || []).length;
        if (serialsCount !== qty) {
          toast.error(
            `Part #${i + 1} (${part.product_name || "Part"}): Please select ${qty} serial number${qty > 1 ? "s" : ""} (currently selected: ${serialsCount})`,
          );
          return;
        }
      }
    }

    // Labor entries validation: start time required, valid range, end time must be after start time
    for (let i = 0; i < laborEntries.length; i++) {
      const l = laborEntries[i];
      if (l.end_time && !l.start_time) {
        toast.error(
          `Labor entry #${i + 1}: Start time is required when end time is provided`,
        );
        return;
      }
      if (l.start_time) {
        if (!l.technician_id || !l.hourly_rate) {
          toast.error(
            `Labor entry #${i + 1}: Technician and hourly rate are required`,
          );
          return;
        }
        const start = new Date(l.start_time);
        if (isNaN(start.getTime())) {
          toast.error(`Labor entry #${i + 1}: Invalid start time`);
          return;
        }
        if (l.end_time) {
          const end = new Date(l.end_time);
          if (isNaN(end.getTime()) || end <= start) {
            toast.error(
              `Labor entry #${i + 1}: End time must be after start time`,
            );
            return;
          }
        }
      }
    }

    setIsSubmitting(true);

    const base = {
      customer_id: selectedCustomerId,
      store_id: currentStore?.id,
      technician_id: technicianId || currentUser?.employee_id || null,
      item_description: itemDescription,
      serial_number: serialNumber || null,
      device_password: devicePassword || null,
      priority,
      diagnosis: diagnosis || null,
      estimated_cost: parseFloat(estimatedCost) || null,
      estimated_completion_date: estimatedCompletionDate || null,
      customer_notes: customerNotes || null,
      technician_notes: technicianNotes || null,
      repair_category_ids: repairCategoryIds,
    };

    const endpoint = isFromScratch
      ? API_ENDPOINTS.REPAIRS.JOBS
      : API_ENDPOINTS.REPAIRS.CREATE_JOB(selectedRepair.id);

    const payload = {
      ...base,
      issue_description: issueDescription || selectedRepair?.issue_description,
    };

    try {
      const res = await apiClient.post(endpoint, payload);
      const job = res.data.data;
      const jobId = job?.id;

      // Post parts, labor, and special parts in parallel within each group
      await Promise.all(
        selectedParts.map((part) =>
          apiClient.post(API_ENDPOINTS.REPAIRS.PARTS(jobId), {
            product_id: part.product_id,
            inventory_batch_id: part.inventory_batch_id || null,
            quantity_used: parseFloat(part.quantity_used),
            unit_cost: parseFloat(part.unit_cost),
            notes: part.notes || null,
            serial_numbers:
              part.serial_numbers?.length > 0 ? part.serial_numbers : null,
          }),
        ),
      );

      await Promise.all(
        laborEntries
          .filter((l) => l.start_time)
          .map((labor) =>
            apiClient.post(API_ENDPOINTS.REPAIRS.LABOR(jobId), {
              technician_id: labor.technician_id,
              start_time: new Date(labor.start_time).toISOString(),
              end_time: labor.end_time
                ? new Date(labor.end_time).toISOString()
                : null,
              hourly_rate: parseFloat(labor.hourly_rate),
              description: labor.description || null,
            }),
          ),
      );

      await Promise.all(
        specialParts.map((sp) =>
          apiClient.post(
            API_ENDPOINTS.REPAIRS.REPAIR_JOB_SPECIAL_PARTS(jobId),
            {
              item_name: sp.item_name,
              brand_id: Number(sp.brand_id),
              model_id: Number(sp.model_id),
              required_quantity: parseInt(sp.required_quantity),
              unit_cost: parseFloat(sp.unit_cost),
              retail_price: sp.retail_price
                ? parseFloat(sp.retail_price)
                : null,
              supplier_id: sp.supplier_id,
              order_link: sp.order_link || null,
              tracking_id: sp.tracking_id || null,
              order_notes: sp.order_notes || null,
              order_date: sp.order_date || null,
              status: sp.status || "pending",
              is_tax_exclusive: sp.is_tax_exclusive || false,
              has_purchase_order: sp.has_purchase_order || false,
              purchase_order_id: sp.purchase_order_id || null,
            },
          ),
        ),
      );

      toast.success(
        `Repair job created! Job ID: ${job?.repair_number || "N/A"}`,
      );
      navigate(all_routes.repairDashboard);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create repair job");
    } finally {
      setIsSubmitting(false);
    }
  };

  const customerNameLabel = (c) => {
    if (!c) return "";
    const name =
      c.customer_type !== "individual" && c.company ? c.company : c.full_name;
    return [name, c.phone].filter(Boolean).join(" - ");
  };

  const handleResetForm = () => {
    setSelectedRepair(null);
    setSelectedExistingRepair(null);
    setIsFromScratch(false);
    setItemDescription("");
    setSerialNumber("");
    setDevicePassword("");
    setIssueDescription("");
    setDiagnosis("");
    setEstimatedCost("");
    setEstimatedCompletionDate("");
    setRepairCategoryIds([]);
    setCustomerNotes("");
    setTechnicianNotes("");
    setSelectedParts([]);
    setLaborEntries([]);
    setSpecialParts([]);
  };

  const renderActionButtons = () => {
    if (selectedExistingRepair) {
      const balanceDue = getRepairBalanceDue(selectedExistingRepair);
      return (
        <div className="w-full space-y-2">
          {/* Quick Management Row: Status, Parts, Labor */}
          <div className="grid grid-cols-3 gap-2">
            <Button
              variant="outline"
              className="h-10 border border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300 hover:bg-blue-600 hover:text-white rounded-lg transition-all flex items-center justify-center gap-1.5 text-xs font-semibold cursor-pointer shadow-xs active:scale-[0.98]"
              onClick={() => handleOpenStatusUpdate(selectedExistingRepair)}
              disabled={!hasPermission("repair job update_status")}
              title="Update Job Status"
            >
              <RefreshCw size={14} />
              <span>Status</span>
            </Button>

            <Button
              variant="outline"
              className="h-10 border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300 hover:bg-amber-600 hover:text-white rounded-lg transition-all flex items-center justify-center gap-1.5 text-xs font-semibold cursor-pointer shadow-xs active:scale-[0.98]"
              onClick={() => handleOpenAddParts(selectedExistingRepair)}
              disabled={!hasPermission("repair job add_part")}
              title="Add Parts to Repair"
            >
              <Package size={14} />
              <span>+ Part</span>
            </Button>

            <Button
              variant="outline"
              className="h-10 border border-cyan-500/30 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-600 hover:text-white rounded-lg transition-all flex items-center justify-center gap-1.5 text-xs font-semibold cursor-pointer shadow-xs active:scale-[0.98]"
              onClick={() => handleOpenAddLabor(selectedExistingRepair)}
              disabled={!hasPermission("repair job add_labor")}
              title="Add Labor Charges"
            >
              <Clock size={14} />
              <span>+ Labor</span>
            </Button>
          </div>

          {/* Core Action Row: Unload, Invoice, Details, Payment */}
          <div className="grid grid-cols-4 gap-2 sm:gap-2.5">
            <Button
              variant="outline"
              className="h-11 border border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive hover:text-white rounded-lg transition-all flex items-center justify-center gap-1.5 text-xs sm:text-sm font-bold cursor-pointer shadow-xs active:scale-[0.98]"
              onClick={() => setSelectedExistingRepair(null)}
              title="Cancel / Unload current repair"
            >
              <X size={15} />
              <span>Unload</span>
            </Button>

            <Button
              variant="outline"
              className="h-11 border border-border bg-muted/80 text-foreground hover:bg-muted rounded-lg transition-all flex items-center justify-center gap-1.5 text-xs sm:text-sm font-bold cursor-pointer shadow-xs active:scale-[0.98]"
              onClick={() => {
                if (pdfBlobUrl) {
                  URL.revokeObjectURL(pdfBlobUrl);
                  setPdfBlobUrl(null);
                }
                setPdfRepairData(selectedExistingRepair);
                setPdfModalOpen(true);
              }}
              title="Generate Documents (Invoice, Job Note, Label)"
            >
              <Printer size={15} />
              <span>Invoice</span>
            </Button>

            <Button
              variant="outline"
              className="h-11 border border-border bg-muted/80 text-foreground hover:bg-muted rounded-lg transition-all flex items-center justify-center gap-1.5 text-xs sm:text-sm font-bold cursor-pointer shadow-xs active:scale-[0.98]"
              onClick={() => {
                setDetailsRepair(selectedExistingRepair);
                setDetailsModalOpen(true);
              }}
              title="View Full Repair Details & Notes"
            >
              <Eye size={15} />
              <span>Details</span>
            </Button>

            {!isRepairPaidInFull(selectedExistingRepair) ? (
              <Button
                className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all flex items-center justify-center gap-1.5 text-xs sm:text-sm font-bold cursor-pointer shadow-md active:scale-[0.98] border-none"
                onClick={() => {
                  setPaymentRepair(selectedExistingRepair);
                  setPaymentModalOpen(true);
                }}
                disabled={!hasPermission("repair job add_payment")}
                title={
                  balanceDue > 0
                    ? `Record Payment (Due: ${currencySymbol}${balanceDue.toFixed(2)})`
                    : "Record Payment / Deposit"
                }
              >
                <CreditCard size={16} />
                <span>Payment</span>
              </Button>
            ) : (
              <Button
                variant="outline"
                className="h-11 border border-border bg-muted text-muted-foreground rounded-lg transition-all flex items-center justify-center gap-1.5 text-xs sm:text-sm font-bold cursor-default shadow-xs"
                disabled
                title="Paid in Full"
              >
                <CheckCircle2 size={16} className="text-emerald-500" />
                <span>Paid</span>
              </Button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="w-full space-y-1.5">
        <div className="grid grid-cols-4 gap-2 sm:gap-2.5">
          <Button
            variant="outline"
            className="h-12 border border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive hover:text-white rounded-lg transition-all flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-bold cursor-pointer shadow-xs active:scale-[0.98]"
            onClick={handleResetForm}
            title="Reset form"
            disabled={!selectedRepair && !isFromScratch && !itemDescription}
          >
            <X size={16} />
            <span>Cancel</span>
          </Button>

          <Button
            className="col-span-3 h-12 text-sm font-bold bg-primary hover:bg-primary/90 text-primary-foreground shadow-md flex items-center justify-center gap-2 rounded-lg cursor-pointer transition-all active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleCreateRepair}
            disabled={
              isSubmitting ||
              !selectedCustomerId ||
              (!selectedRepair && !isFromScratch)
            }
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                <span>Creating Repair Job...</span>
              </>
            ) : (
              <>
                <Wrench className="mr-2 h-5 w-5" />
                <span>Create Repair Job</span>
              </>
            )}
          </Button>
        </div>
        {!selectedCustomerId && (
          <p className="text-xs font-medium text-amber-600 dark:text-amber-400 text-center">
            • Select a customer to continue
          </p>
        )}
        {selectedCustomerId && !selectedRepair && !isFromScratch && (
          <p className="text-xs font-medium text-muted-foreground text-center">
            • Select a repair template or create from scratch
          </p>
        )}
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Render - Left Panel
  // -------------------------------------------------------------------------
  const renderLeftPanel = () => (
    <div className="flex flex-col h-full bg-card border-r border-border shadow-sm">
      {/* Header - mirrors POS CartSidebar */}
      <div className="flex items-center gap-2 p-3 border-b border-border bg-background">
        <Link to="/">
          <Button variant="ghost" size="sm" className="h-10 px-3">
            <ArrowLeft size={18} className="mr-1.5" />
            <span className="text-sm">Dashboard</span>
          </Button>
        </Link>
        <div className="flex-1 text-center">
          <h2 className="font-semibold text-base">Repair Jobs</h2>
        </div>
        <div className="w-[85px] flex justify-end">
          <Button
            variant="outline"
            size="sm"
            className="lg:hidden h-9 px-2 text-xs gap-1 border-primary/40 text-primary"
            onClick={() => setMobileView("catalog")}
          >
            <List size={14} />
            <span>Catalog</span>
            {pendingPaymentRepairs.length > 0 && (
              <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
            )}
          </Button>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {/* Customer Section - matches POS CartSidebar */}
          <div className="rounded-lg border border-border bg-muted/30 p-3">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                {customer ? (
                  <div className="space-y-1">
                    <h6 className="font-semibold text-sm truncate">
                      {customer.customer_type !== "individual" &&
                        customer.company
                        ? customer.company
                        : customer.full_name}
                    </h6>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      {customer.email && customer.email !== "-" && (
                        <div className="flex items-center gap-1">
                          <Mail size={12} />
                          <span className="truncate max-w-[150px]">
                            {customer.email}
                          </span>
                        </div>
                      )}
                      {customer.phone && (
                        <div className="flex items-center gap-1">
                          <Phone size={12} />
                          <span>{customer.phone}</span>
                        </div>
                      )}
                    </div>
                    {customer.street_address && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground truncate">
                        <MapPin size={12} className="shrink-0" />
                        <span className="truncate">
                          {customer.house_number &&
                            `${customer.house_number}, `}
                          {customer.street_address}
                          {customer.city && `, ${customer.city}`}
                          {customer.postcode && ` ${customer.postcode}`}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground italic">
                    No customer selected
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 ml-2">
                {!customerSearchExpanded ? (
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-11 w-11 shrink-0"
                    onClick={() => setCustomerSearchExpanded(true)}
                    title="Search Customer"
                  >
                    <Search size={20} />
                  </Button>
                ) : (
                  <div className="w-[200px]">
                    <ReactSelectLib
                      classNamePrefix="react-select"
                      menuPortalTarget={document.body}
                      isLoading={customersLoading}
                      filterOption={() => true}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (base) => ({
                          ...base,
                          minHeight: "44px",
                          fontSize: "0.875rem",
                        }),
                      }}
                      options={customers.map((c) => ({
                        value: c.id,
                        label: customerNameLabel(c),
                        customerData: c,
                      }))}
                      placeholder="Search..."
                      onInputChange={(v) => {
                        if (customerSearchTimeoutRef.current)
                          clearTimeout(customerSearchTimeoutRef.current);
                        customerSearchTimeoutRef.current = setTimeout(() => {
                          if (v.length > 1) setCustomerSearch(v);
                        }, 500);
                        return v;
                      }}
                      onChange={(opt) => {
                        if (opt?.value) {
                          handleSelectCustomer(opt);
                          setCustomerSearchExpanded(false);
                        }
                      }}
                      onBlur={() =>
                        setTimeout(() => setCustomerSearchExpanded(false), 200)
                      }
                      autoFocus
                      menuIsOpen
                    />
                  </div>
                )}
                {hasPermission("customer create") && (
                  <Button
                    size="icon"
                    className="h-11 w-11 shrink-0"
                    onClick={() => setCustomerModalOpen(true)}
                    title="Add New Customer"
                  >
                    <UserPlus size={20} />
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* -- Loaded Existing Repair Job View -- */}
          {selectedExistingRepair && (
            <Card className="border-border shadow-sm overflow-hidden bg-card">
              {/* Header: Repair Number + Status + Close */}
              <div className="p-3.5 border-b border-border bg-muted/20 space-y-2.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-0.5 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-base text-foreground">
                        {selectedExistingRepair.repair_number || `#${selectedExistingRepair.id}`}
                      </span>
                      {getStatusBadge(selectedExistingRepair.status)}
                      {hasPermission("repair job read") && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 px-1.5 text-[10px] gap-1 text-muted-foreground hover:text-foreground"
                          onClick={() => handleOpenStatusHistory(selectedExistingRepair)}
                          title="View Status History"
                        >
                          <Clock size={10} />
                          <span>History</span>
                        </Button>
                      )}
                      <Badge
                        variant={
                          (selectedExistingRepair.payment_status || "").toLowerCase() === "partially-paid" ||
                            (selectedExistingRepair.payment_status || "").toLowerCase() === "partially_paid" ||
                            (selectedExistingRepair.payment_status || "").toLowerCase() === "partial"
                            ? "info"
                            : isRepairPaidInFull(selectedExistingRepair)
                              ? "success"
                              : "warning"
                        }
                        className="text-[10px] px-1.5 py-0 h-4 font-semibold capitalize"
                      >
                        {(selectedExistingRepair.payment_status || "").toLowerCase() === "partially-paid" ||
                          (selectedExistingRepair.payment_status || "").toLowerCase() === "partially_paid" ||
                          (selectedExistingRepair.payment_status || "").toLowerCase() === "partial"
                          ? "Partially Paid"
                          : isRepairPaidInFull(selectedExistingRepair)
                            ? "Paid"
                            : "Pending Payment"}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        Created: {fmtDate(selectedExistingRepair.created_at)}
                      </span>
                      {selectedExistingRepair.estimated_completion_date && (
                        <span>
                          • Est. Completion: {fmtDate(selectedExistingRepair.estimated_completion_date)}
                        </span>
                      )}
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-foreground shrink-0"
                    onClick={() => setSelectedExistingRepair(null)}
                    title="Unload repair"
                  >
                    <X size={15} />
                  </Button>
                </div>
              </div>

              <CardContent className="p-3.5 space-y-3.5">
                {/* Device & Description Section */}
                <div className="rounded-lg border border-border/80 bg-muted/20 p-3 space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[10px] font-bold text-muted-foreground uppercase flex items-center gap-1">
                      <Wrench size={12} className="text-primary" /> Device / Item
                    </span>
                    {selectedExistingRepair.serial_number && (
                      <span className="font-mono text-[11px] text-muted-foreground">
                        SN: {selectedExistingRepair.serial_number}
                      </span>
                    )}
                  </div>
                  <p className="font-semibold text-sm text-foreground">
                    {selectedExistingRepair.item_description || "No device description provided"}
                  </p>

                  {/* Repair Categories */}
                  {selectedExistingRepair.repair_categories?.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {selectedExistingRepair.repair_categories.map((c) => (
                        <Badge
                          key={c.id}
                          variant="secondary"
                          className="text-[10px] px-1.5 py-0 h-4"
                        >
                          {c.name}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Technician & Priority info */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="p-2.5 rounded-lg border border-border/70 bg-muted/20 space-y-0.5">
                    <span className="text-[9px] uppercase font-bold text-muted-foreground block">
                      Lead Technician
                    </span>
                    <span className="font-medium text-foreground truncate block">
                      {selectedExistingRepair.technician?.full_name || "Unassigned"}
                    </span>
                  </div>
                  <div className="p-2.5 rounded-lg border border-border/70 bg-muted/20 space-y-1">
                    <span className="text-[9px] uppercase font-bold text-muted-foreground block">
                      Priority
                    </span>
                    <div>{getPriorityBadge(selectedExistingRepair.priority)}</div>
                  </div>
                </div>

                {/* Reported Issue */}
                {selectedExistingRepair.issue_description && (
                  <div className="space-y-1">
                    <span className="text-[10px] font-bold text-muted-foreground uppercase flex items-center gap-1">
                      <AlertCircle size={11} className="text-amber-500" /> Reported Issue
                    </span>
                    <div className="p-2.5 rounded-lg bg-muted/20 border border-border text-xs leading-relaxed text-foreground">
                      {selectedExistingRepair.issue_description}
                    </div>
                  </div>
                )}

                {/* Technical Diagnosis */}
                {selectedExistingRepair.diagnosis && (
                  <div className="space-y-1">
                    <span className="text-[10px] font-bold text-muted-foreground uppercase flex items-center gap-1">
                      <Settings size={11} className="text-primary" /> Technical Diagnosis
                    </span>
                    <div className="p-2.5 rounded-lg bg-muted/20 border border-border text-xs leading-relaxed italic text-foreground/90">
                      {selectedExistingRepair.diagnosis}
                    </div>
                  </div>
                )}

                {/* Notes (Customer / Technician) */}
                {(selectedExistingRepair.customer_notes || selectedExistingRepair.technician_notes) && (
                  <div className="space-y-2">
                    {selectedExistingRepair.customer_notes && (
                      <div className="space-y-1">
                        <span className="text-[10px] font-bold text-muted-foreground uppercase">
                          Customer Notes
                        </span>
                        <div className="p-2 rounded bg-muted/20 border border-border/70 text-xs">
                          {selectedExistingRepair.customer_notes}
                        </div>
                      </div>
                    )}
                    {selectedExistingRepair.technician_notes && (
                      <div className="space-y-1">
                        <span className="text-[10px] font-bold text-muted-foreground uppercase">
                          Internal Notes
                        </span>
                        <div className="p-2 rounded bg-muted/20 border border-border/70 text-xs text-muted-foreground">
                          {selectedExistingRepair.technician_notes}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Parts Used */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-[10px] font-bold text-muted-foreground uppercase">
                    <span className="flex items-center gap-1">
                      <Package size={11} /> Parts Used ({selectedExistingRepair.parts?.length || 0})
                    </span>
                    <div className="flex items-center gap-1.5">
                      <span>
                        Total: {currencySymbol}
                        {parseFloat(selectedExistingRepair.parts_cost || 0).toFixed(2)}
                      </span>
                      {hasPermission("repair job add_part") && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-5 px-1.5 text-[10px] gap-1 font-semibold border-amber-500/30 text-amber-700 dark:text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 cursor-pointer"
                          onClick={() => handleOpenAddParts(selectedExistingRepair)}
                          title="Add Part to Repair"
                        >
                          <Plus size={10} /> Part
                        </Button>
                      )}
                      {selectedExistingRepair.parts?.length > 0 && hasPermission("repair job read") && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 px-1.5 text-[10px] gap-1 text-primary hover:bg-primary/5 cursor-pointer"
                          onClick={() => handleOpenViewParts(selectedExistingRepair)}
                          title="View All Parts"
                        >
                          <Settings size={10} /> View All
                        </Button>
                      )}
                    </div>
                  </div>
                  {selectedExistingRepair.parts?.length > 0 ? (
                    <div className="divide-y divide-border rounded-lg border border-border/70 overflow-hidden bg-muted/10">
                      {selectedExistingRepair.parts.map((part, idx) => (
                        <div
                          key={part.id || idx}
                          className="flex items-center justify-between p-2 text-xs"
                        >
                          <div className="min-w-0 flex-1 pr-2">
                            <span className="font-medium text-foreground block truncate">
                              {part.product?.name || `Part #${idx + 1}`}
                            </span>
                            {part.notes && (
                              <span className="text-[10px] text-muted-foreground block truncate">
                                {part.notes}
                              </span>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            <span className="text-[11px] text-muted-foreground block">
                              {parseFloat(part.quantity_used || 1)} × {currencySymbol}
                              {parseFloat(part.unit_cost || 0).toFixed(2)}
                            </span>
                            <span className="font-semibold text-foreground">
                              {currencySymbol}
                              {parseFloat(part.total_cost || 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-2.5 rounded-lg border border-dashed border-border/80 bg-muted/10 text-xs text-muted-foreground">
                      <span className="italic">No parts added yet</span>
                      {hasPermission("repair job add_part") && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-[11px] text-primary gap-1 cursor-pointer hover:bg-primary/5"
                          onClick={() => handleOpenAddParts(selectedExistingRepair)}
                        >
                          <Plus size={12} /> Add First Part
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* Labor Entries */}
                {(() => {
                  const laborEntriesList =
                    selectedExistingRepair.labor_entries ||
                    selectedExistingRepair.laborEntries ||
                    [];
                  return (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-[10px] font-bold text-muted-foreground uppercase">
                        <span className="flex items-center gap-1">
                          <Clock size={11} /> Labor Entries ({laborEntriesList.length})
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span>
                            Total: {currencySymbol}
                            {parseFloat(selectedExistingRepair.labor_cost || 0).toFixed(2)}
                          </span>
                          {hasPermission("repair job add_labor") && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-5 px-1.5 text-[10px] gap-1 font-semibold border-cyan-500/30 text-cyan-700 dark:text-cyan-400 bg-cyan-500/10 hover:bg-cyan-500/20 cursor-pointer"
                              onClick={() => handleOpenAddLabor(selectedExistingRepair)}
                              title="Add Labor Charge"
                            >
                              <Plus size={10} /> Labor
                            </Button>
                          )}
                          {laborEntriesList.length > 0 && hasPermission("repair job read") && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 px-1.5 text-[10px] gap-1 text-primary hover:bg-primary/5 cursor-pointer"
                              onClick={() => handleOpenViewLabor(selectedExistingRepair)}
                              title="View All Labor"
                            >
                              <Users size={10} /> View All
                            </Button>
                          )}
                        </div>
                      </div>
                      {laborEntriesList.length > 0 ? (
                        <div className="divide-y divide-border rounded-lg border border-border/70 overflow-hidden bg-muted/10">
                          {laborEntriesList.map((entry, idx) => (
                            <div
                              key={entry.id || idx}
                              className="flex items-center justify-between p-2 text-xs"
                            >
                              <div className="min-w-0 flex-1 pr-2">
                                <span className="font-medium text-foreground block truncate">
                                  {entry.technician?.full_name || `Technician #${idx + 1}`}
                                </span>
                                {entry.description && (
                                  <span className="text-[10px] text-muted-foreground block truncate">
                                    {entry.description}
                                  </span>
                                )}
                              </div>
                              <div className="text-right shrink-0">
                                <span className="text-[11px] text-muted-foreground block">
                                  {entry.formatted_duration || `${entry.duration_minutes || 0}m`} @ {currencySymbol}
                                  {parseFloat(entry.hourly_rate || 0).toFixed(2)}/hr
                                </span>
                                <span className="font-semibold text-foreground">
                                  {currencySymbol}
                                  {parseFloat(entry.total_cost || 0).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center justify-between p-2.5 rounded-lg border border-dashed border-border/80 bg-muted/10 text-xs text-muted-foreground">
                          <span className="italic">No labor charges recorded yet</span>
                          {hasPermission("repair job add_labor") && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-[11px] text-primary gap-1 cursor-pointer hover:bg-primary/5"
                              onClick={() => handleOpenAddLabor(selectedExistingRepair)}
                            >
                              <Plus size={12} /> Add First Labor
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Selected Repair / From Scratch Info */}
          {(selectedRepair || isFromScratch) && !selectedExistingRepair && (
            <Card className="border-primary/30 bg-primary/5">
              <CardHeader className="p-3 pb-0">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <Hammer size={14} className="text-primary" />
                    {isFromScratch ? "Custom Repair" : "Selected Template"}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                    onClick={handleClearSelection}
                  >
                    <X size={13} />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                {selectedRepair && (
                  <p className="text-sm font-medium text-primary mb-3 truncate">
                    {selectedRepair.name}
                  </p>
                )}

                {/* Tab-based form - keeps the panel compact */}
                <Tabs defaultValue="device" className="w-full">
                  <TabsList className="flex w-full h-auto bg-transparent border-b border-border p-0 mb-3 gap-0 overflow-x-auto">
                    {[
                      {
                        value: "device",
                        icon: <Package size={11} />,
                        label: "Device",
                      },
                      {
                        value: "issue",
                        icon: <Settings size={11} />,
                        label: "Issue",
                      },
                      {
                        value: "settings",
                        icon: <Calendar size={11} />,
                        label: "Settings",
                      },
                      {
                        value: "notes",
                        icon: <Edit size={11} />,
                        label: "Notes",
                      },
                      {
                        value: "parts",
                        icon: <Package size={11} />,
                        label: "Parts",
                      },
                      {
                        value: "labor",
                        icon: <Clock size={11} />,
                        label: "Labor",
                      },
                    ].map((tab) => (
                      <TabsTrigger
                        key={tab.value}
                        value={tab.value}
                        className="flex-1 min-w-[60px] flex items-center justify-center gap-1 text-[11px] font-medium px-2 h-8 rounded-none border-b-2 border-transparent bg-transparent shadow-none whitespace-nowrap data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {tab.icon}
                        {tab.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  {/* Device tab */}
                  <TabsContent value="device" className="space-y-2 mt-0">
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">
                        Item Description{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        className="h-8 text-xs"
                        placeholder="iPhone 14 Pro Max, MacBook Air M2..."
                        maxLength={255}
                        value={itemDescription}
                        onChange={(e) => setItemDescription(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">
                        Serial Number
                      </Label>
                      <Input
                        className="h-8 text-xs"
                        placeholder="Device serial number"
                        value={serialNumber}
                        onChange={(e) => setSerialNumber(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">
                        Access Code / Password
                      </Label>
                      <Input
                        className="h-8 text-xs"
                        placeholder="PIN or password"
                        value={devicePassword}
                        onChange={(e) => setDevicePassword(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">
                        Repair Categories
                      </Label>
                      <ReactSelect
                        isMulti
                        options={categories.map((c) => ({
                          value: c.id,
                          label: c.name,
                        }))}
                        value={categories
                          .filter((c) =>
                            repairCategoryIds
                              .map(String)
                              .includes(String(c.id)),
                          )
                          .map((c) => ({ value: c.id, label: c.name }))}
                        onChange={(opts) =>
                          setRepairCategoryIds((opts || []).map((o) => o.value))
                        }
                        placeholder="Select categories..."
                        className="text-xs"
                      />
                    </div>
                  </TabsContent>

                  {/* Issue tab */}
                  <TabsContent value="issue" className="space-y-2 mt-0">
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">
                        Reported Issue{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Textarea
                        className="text-xs resize-none"
                        rows={4}
                        placeholder="Describe the reported issue..."
                        value={issueDescription}
                        onChange={(e) => setIssueDescription(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">
                        Internal Diagnosis
                      </Label>
                      <Textarea
                        className="text-xs resize-none"
                        rows={4}
                        placeholder="Technician diagnosis notes..."
                        value={diagnosis}
                        onChange={(e) => setDiagnosis(e.target.value)}
                      />
                    </div>
                  </TabsContent>

                  {/* Settings tab */}
                  <TabsContent value="settings" className="space-y-2 mt-0">
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">
                        Service Priority
                      </Label>
                      <div className="grid grid-cols-4 gap-1">
                        {["low", "normal", "high", "urgent"].map((p) => (
                          <Button
                            key={p}
                            type="button"
                            size="sm"
                            variant={priority === p ? "default" : "secondary"}
                            className="capitalize text-xs h-7 px-1"
                            onClick={() => setPriority(p)}
                          >
                            {p}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">
                        Lead Technician
                      </Label>
                      <ReactSelect
                        options={employees.map((e) => ({
                          value: String(e.id),
                          label: e.full_name,
                        }))}
                        value={
                          technicianId
                            ? employees
                              .filter((e) => String(e.id) === technicianId)
                              .map((e) => ({
                                value: String(e.id),
                                label: e.full_name,
                              }))[0] || null
                            : null
                        }
                        onChange={(opt) =>
                          setTechnicianId(opt ? opt.value : "")
                        }
                        placeholder="Assign technician..."
                        isClearable
                        className="text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">
                        Estimated Cost ({currencySymbol})
                      </Label>
                      <Input
                        className="h-8 text-xs"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={estimatedCost}
                        onChange={(e) => setEstimatedCost(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">
                        Est. Completion
                      </Label>
                      <Input
                        className="h-8 text-xs"
                        type="datetime-local"
                        value={estimatedCompletionDate}
                        onChange={(e) =>
                          setEstimatedCompletionDate(e.target.value)
                        }
                      />
                    </div>
                  </TabsContent>

                  {/* Notes tab */}
                  <TabsContent value="notes" className="space-y-2 mt-0">
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">
                        Customer Facing Notes
                      </Label>
                      <Textarea
                        className="text-xs resize-none"
                        rows={4}
                        placeholder="Notes visible to the customer..."
                        value={customerNotes}
                        onChange={(e) => setCustomerNotes(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">
                        Internal Technician Notes
                      </Label>
                      <Textarea
                        className="text-xs resize-none"
                        rows={4}
                        placeholder="Internal notes for technicians..."
                        value={technicianNotes}
                        onChange={(e) => setTechnicianNotes(e.target.value)}
                      />
                    </div>
                  </TabsContent>

                  {/* Parts tab */}
                  <TabsContent value="parts" className="space-y-2 mt-0">
                    {/* Read-only template parts */}
                    {selectedRepair &&
                      (selectedRepair.parts_n_components || []).length > 0 && (
                        <div className="space-y-1">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                            Included from template
                          </p>
                          {(selectedRepair.parts_n_components || []).map(
                            (p, i) => {
                              const product =
                                templatePartProducts.find(
                                  (prod) => prod.id === p.part_id,
                                ) ||
                                posProducts.find(
                                  (prod) => prod.id === p.part_id,
                                );
                              const partName =
                                product?.name ||
                                `Part ${i + 1} (...${String(p.part_id).slice(-6)})`;
                              return (
                                <div
                                  key={i}
                                  className="flex items-center justify-between text-xs bg-muted/40 border border-dashed rounded px-2 py-1"
                                >
                                  <span className="text-muted-foreground truncate flex-1">
                                    {partName}
                                  </span>
                                  <span className="text-muted-foreground mx-2 shrink-0">
                                    x{p.quantity}
                                  </span>
                                  <span className="font-medium shrink-0">
                                    {currencySymbol}
                                    {parseFloat(p.cost || 0).toFixed(2)}
                                  </span>
                                </div>
                              );
                            },
                          )}
                          <Separator className="my-1" />
                        </div>
                      )}
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">
                        Add Extra Part
                      </Label>
                      <ReactSelect
                        options={posProducts.map((p) => ({
                          value: p.id,
                          label: `${p.name}${p.sku ? ` (${p.sku})` : ""}`,
                          product: p,
                        }))}
                        placeholder="Search products..."
                        onInputChange={(v) => setProductSearch(v)}
                        onChange={(opt) => {
                          if (!opt) return;
                          const p = opt.product;
                          setSelectedParts((prev) => [
                            ...prev,
                            {
                              product_id: p.id,
                              product_name: p.name,
                              sku: p.sku,
                              quantity_used: 1,
                              unit_cost: parseFloat(p.sell_price || 0),
                              inventory_batch_id: null,
                              inventory_batch_number: "",
                              serial_numbers: [],
                              notes: "",
                              tracking_type:
                                p.tracking_type ||
                                (p.is_serialized
                                  ? "serial"
                                  : p.has_batches
                                    ? "standard"
                                    : "none"),
                            },
                          ]);
                        }}
                        value={null}
                        isClearable={false}
                        isLoading={productsLoading}
                        className="text-xs"
                      />
                    </div>

                    {selectedParts.length > 0 ? (
                      <div className="space-y-2">
                        {selectedParts.map((part, idx) => (
                          <div
                            key={idx}
                            className="border rounded-md p-2 space-y-2 bg-muted/20"
                          >
                            <div className="flex items-center justify-between gap-1">
                              <span className="text-xs font-medium truncate flex-1">
                                {part.product_name}
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 text-destructive hover:text-destructive shrink-0"
                                onClick={() =>
                                  setSelectedParts((prev) =>
                                    prev.filter((_, i) => i !== idx),
                                  )
                                }
                              >
                                <Trash2 size={11} />
                              </Button>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-0.5">
                                <Label className="text-[10px]">Qty</Label>
                                <Input
                                  className="h-7 text-xs"
                                  type="number"
                                  min="0.01"
                                  step="0.01"
                                  value={part.quantity_used}
                                  onChange={(e) => {
                                    const np = [...selectedParts];
                                    np[idx] = {
                                      ...np[idx],
                                      quantity_used: e.target.value,
                                    };
                                    setSelectedParts(np);
                                  }}
                                />
                              </div>
                              <div className="space-y-0.5">
                                <Label className="text-[10px]">
                                  Unit Cost ({currencySymbol})
                                </Label>
                                <Input
                                  className="h-7 text-xs"
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={part.unit_cost}
                                  onChange={(e) => {
                                    const np = [...selectedParts];
                                    np[idx] = {
                                      ...np[idx],
                                      unit_cost: e.target.value,
                                    };
                                    setSelectedParts(np);
                                  }}
                                />
                              </div>
                            </div>
                            {part.tracking_type !== "none" && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className={`h-7 text-xs w-full ${(part.tracking_type === "standard" &&
                                  !part.inventory_batch_id) ||
                                  (part.tracking_type === "serial" &&
                                    (!part.serial_numbers ||
                                      part.serial_numbers.length !==
                                      parseFloat(part.quantity_used || 1)))
                                  ? "border-destructive text-destructive hover:bg-destructive/5"
                                  : "border-emerald-500 text-emerald-700 bg-emerald-50/40 hover:bg-emerald-50/60"
                                  }`}
                                onClick={() => {
                                  setInventorySelectorTarget({
                                    partIndex: idx,
                                    type:
                                      part.tracking_type === "serial"
                                        ? "serial"
                                        : "standard",
                                    productId: part.product_id,
                                  });
                                  setInventorySelectorOpen(true);
                                }}
                              >
                                {part.tracking_type === "serial" ? (
                                  <>
                                    <ScanBarcode className="mr-1 h-3.5 w-3.5" />
                                    Serials: {part.serial_numbers?.length || 0}/
                                    {part.quantity_used || 1}
                                  </>
                                ) : (
                                  <>
                                    <Layers className="mr-1 h-3.5 w-3.5" />
                                    {part.inventory_batch_number ||
                                      (part.inventory_batch_id
                                        ? "Batch Selected"
                                        : "Select Batch *")}
                                  </>
                                )}
                              </Button>
                            )}
                            <div className="space-y-0.5">
                              <Label className="text-[10px]">Notes</Label>
                              <Input
                                className="h-7 text-xs"
                                placeholder="Optional..."
                                value={part.notes}
                                onChange={(e) => {
                                  const np = [...selectedParts];
                                  np[idx] = {
                                    ...np[idx],
                                    notes: e.target.value,
                                  };
                                  setSelectedParts(np);
                                }}
                              />
                            </div>
                            <div className="text-[10px] text-right text-muted-foreground">
                              Subtotal: {currencySymbol}
                              {(
                                parseFloat(part.quantity_used || 0) *
                                parseFloat(part.unit_cost || 0)
                              ).toFixed(2)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center py-3 text-[11px] text-muted-foreground">
                        No parts added yet.
                      </p>
                    )}

                    {/* Special parts */}
                    {hasPermission("special part create") && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full text-xs h-7 border-dashed"
                        onClick={() => setSpecialPartModalOpen(true)}
                      >
                        <Plus size={12} className="mr-1" /> Add Special /
                        Non-Stock Part
                      </Button>
                    )}

                    {specialParts.length > 0 && (
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
                          Special Parts
                        </Label>
                        {specialParts.map((sp, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between border rounded p-2 bg-orange-50 dark:bg-orange-950/20 gap-2"
                          >
                            <span className="text-xs font-medium flex-1 truncate">
                              {sp.item_name}
                            </span>
                            <span className="text-[10px] text-muted-foreground shrink-0">
                              x{sp.required_quantity}
                            </span>
                            <span className="text-xs font-medium shrink-0">
                              {currencySymbol}
                              {(
                                parseFloat(sp.unit_cost || 0) *
                                sp.required_quantity
                              ).toFixed(2)}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 text-destructive shrink-0"
                              onClick={() =>
                                setSpecialParts((prev) =>
                                  prev.filter((_, i) => i !== idx),
                                )
                              }
                            >
                              <Trash2 size={11} />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  {/* Labor tab */}
                  <TabsContent value="labor" className="space-y-2 mt-0">
                    {/* Read-only template labor */}
                    {selectedRepair &&
                      (selectedRepair.labor_n_timetracking || []).length >
                      0 && (
                        <div className="space-y-1">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                            Included from template
                          </p>
                          {(selectedRepair.labor_n_timetracking || []).map(
                            (l, i) => {
                              const tech = employees.find(
                                (e) => String(e.id) === String(l.labor_id),
                              );
                              const techName =
                                tech?.full_name || `Technician ${i + 1}`;
                              return (
                                <div
                                  key={i}
                                  className="flex items-center justify-between text-xs bg-muted/40 border border-dashed rounded px-2 py-1"
                                >
                                  <span className="text-muted-foreground truncate flex-1">
                                    {techName}
                                    {l.description ? ` - ${l.description}` : ""}
                                  </span>
                                  <span className="font-medium shrink-0">
                                    {currencySymbol}
                                    {parseFloat(l.hourly_rate || 0).toFixed(2)}
                                    /hr
                                  </span>
                                </div>
                              );
                            },
                          )}
                          <Separator className="my-1" />
                        </div>
                      )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full h-7 text-xs border-dashed"
                      onClick={() =>
                        setLaborEntries((prev) => [
                          ...prev,
                          {
                            technician_id: technicianId || "",
                            hourly_rate: technicianId
                              ? employees.find(
                                (e) => String(e.id) === technicianId,
                              )?.hourly_rate || ""
                              : "",
                            start_time: "",
                            end_time: "",
                            description: "",
                          },
                        ])
                      }
                    >
                      <Plus size={12} className="mr-1" /> Add Labor Entry
                    </Button>

                    {laborEntries.length > 0 ? (
                      <div className="space-y-2">
                        {laborEntries.map((labor, idx) => (
                          <div
                            key={idx}
                            className="border rounded-md p-2 space-y-2 bg-muted/20"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium">
                                Entry #{idx + 1}
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 text-destructive hover:text-destructive"
                                onClick={() =>
                                  setLaborEntries((prev) =>
                                    prev.filter((_, i) => i !== idx),
                                  )
                                }
                              >
                                <Trash2 size={11} />
                              </Button>
                            </div>
                            <div className="space-y-0.5">
                              <Label className="text-[10px]">
                                Technician{" "}
                                <span className="text-destructive">*</span>
                              </Label>
                              <ReactSelect
                                options={employees.map((e) => ({
                                  value: String(e.id),
                                  label: e.full_name,
                                  hourly_rate: e.hourly_rate,
                                }))}
                                value={
                                  labor.technician_id
                                    ? employees
                                      .filter(
                                        (e) =>
                                          String(e.id) ===
                                          labor.technician_id,
                                      )
                                      .map((e) => ({
                                        value: String(e.id),
                                        label: e.full_name,
                                      }))[0] || null
                                    : null
                                }
                                onChange={(opt) => {
                                  const ne = [...laborEntries];
                                  ne[idx] = {
                                    ...ne[idx],
                                    technician_id: opt ? opt.value : "",
                                    hourly_rate:
                                      opt?.hourly_rate ?? ne[idx].hourly_rate,
                                  };
                                  setLaborEntries(ne);
                                }}
                                placeholder="Select technician..."
                                isClearable
                                className="text-xs"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-0.5">
                                <Label className="text-[10px]">
                                  Rate ({currencySymbol}/hr){" "}
                                  <span className="text-destructive">*</span>
                                </Label>
                                <Input
                                  className="h-7 text-xs"
                                  type="number"
                                  min="0"
                                  max="9999.99"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={labor.hourly_rate}
                                  onChange={(e) => {
                                    const ne = [...laborEntries];
                                    ne[idx] = {
                                      ...ne[idx],
                                      hourly_rate: e.target.value,
                                    };
                                    setLaborEntries(ne);
                                  }}
                                />
                              </div>
                              <div className="space-y-0.5">
                                <Label className="text-[10px]">Duration</Label>
                                <div className="h-7 text-xs flex items-center text-muted-foreground px-1">
                                  {labor.start_time && labor.end_time
                                    ? new Date(labor.end_time) >
                                      new Date(labor.start_time)
                                      ? `${((new Date(labor.end_time) - new Date(labor.start_time)) / 3600000).toFixed(2)} hrs`
                                      : "Invalid range"
                                    : "-"}
                                </div>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-0.5">
                                <Label className="text-[10px]">
                                  Start{" "}
                                  <span className="text-destructive">*</span>
                                </Label>
                                <Input
                                  className={`h-7 text-xs ${labor.end_time && !labor.start_time
                                    ? "border-destructive text-destructive focus-visible:ring-destructive"
                                    : ""
                                    }`}
                                  type="datetime-local"
                                  value={labor.start_time}
                                  onChange={(e) => {
                                    const ne = [...laborEntries];
                                    ne[idx] = {
                                      ...ne[idx],
                                      start_time: e.target.value,
                                    };
                                    setLaborEntries(ne);
                                  }}
                                />
                              </div>
                              <div className="space-y-0.5">
                                <Label className="text-[10px]">End</Label>
                                <Input
                                  className={`h-7 text-xs ${labor.start_time &&
                                    labor.end_time &&
                                    new Date(labor.end_time) <=
                                    new Date(labor.start_time)
                                    ? "border-destructive text-destructive focus-visible:ring-destructive"
                                    : ""
                                    }`}
                                  type="datetime-local"
                                  min={labor.start_time || undefined}
                                  value={labor.end_time}
                                  onChange={(e) => {
                                    const ne = [...laborEntries];
                                    ne[idx] = {
                                      ...ne[idx],
                                      end_time: e.target.value,
                                    };
                                    setLaborEntries(ne);
                                  }}
                                />
                              </div>
                            </div>
                            {labor.end_time && !labor.start_time && (
                              <p className="text-[10px] text-destructive flex items-center gap-1 font-medium">
                                <AlertCircle size={10} /> Start time is required when end time is set
                              </p>
                            )}
                            {labor.start_time &&
                              labor.end_time &&
                              new Date(labor.end_time) <=
                              new Date(labor.start_time) && (
                                <p className="text-[10px] text-destructive flex items-center gap-1 font-medium">
                                  <AlertCircle size={10} /> End time must be after start time
                                </p>
                              )}
                            <div className="space-y-0.5">
                              <Label className="text-[10px]">Description</Label>
                              <Input
                                className="h-7 text-xs"
                                placeholder="Work done..."
                                value={labor.description}
                                onChange={(e) => {
                                  const ne = [...laborEntries];
                                  ne[idx] = {
                                    ...ne[idx],
                                    description: e.target.value,
                                  };
                                  setLaborEntries(ne);
                                }}
                              />
                            </div>
                            {labor.start_time && labor.hourly_rate && (
                              <div className="text-[10px] text-right text-muted-foreground">
                                {labor.end_time
                                  ? new Date(labor.end_time) >
                                    new Date(labor.start_time)
                                    ? `Cost: ${currencySymbol}${(((new Date(labor.end_time) - new Date(labor.start_time)) / 3600000) * parseFloat(labor.hourly_rate)).toFixed(2)}`
                                    : "Invalid time range"
                                  : "In Progress"}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center py-3 text-[11px] text-muted-foreground">
                        No labor entries yet.
                      </p>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          )}

          {/* Empty state hint */}
          {!selectedRepair && !isFromScratch && !selectedExistingRepair && (
            <div className="rounded-lg border border-dashed border-border p-8 text-center text-muted-foreground space-y-1 flex flex-col items-center justify-center">
              <Wrench size={32} className="mx-auto opacity-30 mb-1" />
              <p className="text-xs font-medium text-foreground">No Repair Selected</p>
              <p className="text-xs text-muted-foreground max-w-xs">
                Select an unpaid repair job or predefined template from the right panel to load it here, or create a new job from scratch.
              </p>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Price Summary + Submit - fixed at bottom, mirrors CartSidebar */}
      <div className="bg-background border-t border-border p-2.5 space-y-2.5">
        {/* Price rows */}
        <div className="rounded-xl border border-border/80 bg-muted/20 p-2.5 space-y-2">
          {selectedExistingRepair ? (
            <>
              <div className="flex justify-between items-center text-xs text-muted-foreground">
                <span>Total Repair Cost</span>
                <span className="font-semibold text-foreground tabular-nums">
                  {currencySymbol}
                  {parseFloat(
                    selectedExistingRepair.total_cost ??
                    selectedExistingRepair.estimated_cost ??
                    0,
                  ).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs text-muted-foreground">
                <span>Total Paid to Date</span>
                <span className="font-semibold text-emerald-600 tabular-nums">
                  {currencySymbol}
                  {parseFloat(selectedExistingRepair.paid_amount || 0).toFixed(2)}
                </span>
              </div>
              <Separator className="my-1.5 bg-border/70" />
              <div className="flex justify-between items-center pt-0.5">
                <span className="text-xs font-bold uppercase tracking-wider text-foreground">
                  Balance Due
                </span>
                <span
                  className={`text-xl sm:text-2xl font-black tracking-tight tabular-nums ${getRepairBalanceDue(selectedExistingRepair) > 0
                    ? "text-amber-600 dark:text-amber-400"
                    : isRepairPaidInFull(selectedExistingRepair)
                      ? "text-emerald-600"
                      : "text-foreground"
                    }`}
                >
                  {currencySymbol}
                  {getRepairBalanceDue(selectedExistingRepair).toFixed(2)}
                </span>
              </div>
            </>
          ) : selectedRepair ? (
            <>
              {totals.parts > 0 && (
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Package size={12} />
                    <span>Parts</span>
                  </span>
                  <span className="font-semibold tabular-nums text-foreground">
                    {currencySymbol}
                    {totals.parts.toFixed(2)}
                  </span>
                </div>
              )}
              {totals.labor > 0 && (
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Clock size={12} />
                    <span>Labor</span>
                  </span>
                  <span className="font-semibold tabular-nums text-foreground">
                    {currencySymbol}
                    {totals.labor.toFixed(2)}
                  </span>
                </div>
              )}
              {(totals.parts > 0 || totals.labor > 0) && (
                <Separator className="my-1.5 bg-border/70" />
              )}
              <div className="flex justify-between items-center pt-0.5">
                <span className="text-xs font-bold uppercase tracking-wider text-foreground">
                  Total Estimate
                </span>
                <span className="text-primary text-xl sm:text-2xl font-black tracking-tight tabular-nums">
                  {currencySymbol}
                  {totals.total.toFixed(2)}
                </span>
              </div>
            </>
          ) : isFromScratch ? (
            <>
              {scratchTotals.parts > 0 && (
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Package size={12} />
                    <span>Parts</span>
                  </span>
                  <span className="font-semibold tabular-nums text-foreground">
                    {currencySymbol}
                    {scratchTotals.parts.toFixed(2)}
                  </span>
                </div>
              )}
              {scratchTotals.labor > 0 && (
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Clock size={12} />
                    <span>Labor</span>
                  </span>
                  <span className="font-semibold tabular-nums text-foreground">
                    {currencySymbol}
                    {scratchTotals.labor.toFixed(2)}
                  </span>
                </div>
              )}
              {(scratchTotals.parts > 0 || scratchTotals.labor > 0) && (
                <Separator className="my-1.5 bg-border/70" />
              )}
              <div className="flex justify-between items-center pt-0.5">
                <span className="text-xs font-bold uppercase tracking-wider text-foreground">
                  Total Estimate
                </span>
                <span className="text-primary text-xl sm:text-2xl font-black tracking-tight tabular-nums">
                  {currencySymbol}
                  {(
                    scratchTotals.parts + scratchTotals.labor ||
                    parseFloat(estimatedCost || 0)
                  ).toFixed(2)}
                </span>
              </div>
            </>
          ) : (
            <div className="flex justify-between items-center text-xs text-muted-foreground">
              <span className="text-xs font-bold uppercase tracking-wider">Total Estimate</span>
              <span className="text-xl sm:text-2xl font-black tracking-tight tabular-nums text-foreground">
                {currencySymbol}0.00
              </span>
            </div>
          )}
        </div>

        {/* Action Button: Visible on mobile only */}
        <div className="lg:hidden mt-3">
          {renderActionButtons()}
        </div>
      </div>
    </div>
  );

  // -------------------------------------------------------------------------
  // Render - Right Panel
  // -------------------------------------------------------------------------
  const renderRightPanel = () => {
    const canAccessPredefined =
      hasPermission("predefined repair job read") ||
      hasPermission("predefined repair job create_repair_job");
    const canAccessRegular =
      hasPermission("repair job read") ||
      hasPermission("repair job add_payment");

    return (
      <div className="flex flex-col h-full bg-muted/20 p-2 xl:p-4 overflow-hidden">
        {!canAccessPredefined && !canAccessRegular ? (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground p-8">
            <Wrench size={48} className="mb-4 opacity-20" />
            <p className="text-center font-medium">Restricted Access</p>
            <p className="text-center text-sm mt-2">
              You do not have permission to view repair jobs or predefined repairs.
            </p>
          </div>
        ) : (
          <Tabs
            value={activeTab}
            onValueChange={(v) => {
              setActiveTab(v);
              setCurrentCat(null);
            }}
            className="flex flex-col flex-1 min-h-0 space-y-2 xl:space-y-4"
          >
            {/* Top Bar: 1. Sales  2. Repairs */}
            <div className="flex items-center gap-2 w-full flex-shrink-0">
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden shrink-0 h-10 px-2.5 text-xs gap-1"
                onClick={() => setMobileView("form")}
              >
                <ArrowLeft size={16} />
                <span className="hidden sm:inline">Job Form</span>
              </Button>
              <Tabs value="repairs" className="w-full flex-1">
                <TabsList className="grid w-full grid-cols-4 h-10 p-1 bg-muted rounded-lg border border-border/50 shadow-xs">
                  <TabsTrigger
                    value="sales"
                    onClick={() => navigate(all_routes.pos)}
                    className="flex items-center justify-center gap-2 h-8 text-sm font-medium rounded-md text-muted-foreground hover:text-foreground hover:bg-background/40 transition-all cursor-pointer"
                  >
                    <ShoppingCart size={15} />
                    <span>Sales</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="repairs"
                    className="flex items-center justify-center gap-2 h-8 text-sm font-semibold rounded-md cursor-pointer transition-all"
                  >
                    <Wrench size={15} />
                    <span>Repairs</span>
                  </TabsTrigger>
                  <div
                    title="Feature coming soon"
                    className="flex items-center justify-center cursor-not-allowed"
                  >
                    <TabsTrigger
                      disabled
                      value="trade-in"
                      className="w-full flex items-center justify-center gap-2 h-8 text-sm font-medium rounded-md opacity-50 cursor-not-allowed pointer-events-none"
                    >
                      <ArrowRightLeft size={15} />
                      <span>Trade In</span>
                    </TabsTrigger>
                  </div>
                  <div
                    title="Feature coming soon"
                    className="flex items-center justify-center cursor-not-allowed"
                  >
                    <TabsTrigger
                      disabled
                      value="miscellaneous"
                      className="w-full flex items-center justify-center gap-2 h-8 text-sm font-medium rounded-md opacity-50 cursor-not-allowed pointer-events-none"
                    >
                      <Boxes size={15} />
                      <span>Miscellaneous</span>
                    </TabsTrigger>
                  </div>
                </TabsList>
              </Tabs>
            </div>

            {/* Second Bar: Filters (By Category, Predefined, Repairs, and Add New) */}
            <div className="flex-shrink-0">
              <div className="flex items-center justify-between gap-3 p-1.5 px-2.5 bg-card border border-border rounded-lg shadow-2xs">
                <TabsList
                  className="grid flex-1 max-w-[280px] h-8 p-0.5 bg-muted/80 rounded-md border border-border/40"
                  style={{
                    gridTemplateColumns: `repeat(${canAccessPredefined ? 2 : 1}, minmax(0, 1fr))`,
                  }}
                >
                  {canAccessPredefined && (
                    <TabsTrigger
                      value="by-category"
                      className="flex items-center gap-1.5 text-xs font-medium h-7 rounded-sm data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs text-muted-foreground hover:text-foreground transition-all cursor-pointer"
                    >
                      <LayoutGrid size={14} />
                      <span className="truncate">By Category</span>
                    </TabsTrigger>
                  )}
                  {canAccessPredefined && (
                    <TabsTrigger
                      value="all-repairs"
                      className="flex items-center gap-1.5 text-xs font-medium h-7 rounded-sm data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs text-muted-foreground hover:text-foreground transition-all cursor-pointer"
                    >
                      <List size={14} />
                      <span className="truncate">Repairs</span>
                    </TabsTrigger>
                  )}
                </TabsList>
                {canAccessPredefined && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 h-8 text-xs px-2.5 gap-1.5 font-medium border-border hover:border-primary hover:text-primary transition-colors cursor-pointer"
                    onClick={() => {
                      handleFromScratch();
                      setMobileView("form");
                    }}
                  >
                    <Plus size={14} />
                    <span>Add New</span>
                  </Button>
                )}
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 min-h-0">
              {/* -- By Category -- */}
              <TabsContent
                value="by-category"
                className="h-full m-0 data-[state=active]:flex flex-col"
              >
                {!currentCat ? (
                  // Category grid
                  <ScrollArea className="h-full pr-2">
                    {categoriesLoading ? (
                      <CategoryCardSkeleton />
                    ) : categories.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                        <Folder size={48} className="mb-3 opacity-20" />
                        <p className="text-sm">No repair categories found.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2 xl:gap-4 pb-4">
                        {categories.slice(0, 25).map((cat) => (
                          <RepairCategoryCard
                            key={cat.id}
                            category={cat}
                            onSelect={setCurrentCat}
                          />
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                ) : (
                  // Predefined repairs for selected category
                  <div className="flex flex-col h-full overflow-hidden">
                    <div className="flex items-center justify-between pb-3 flex-shrink-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentCat(null)}
                          className="h-8 gap-1.5"
                        >
                          <ChevronLeft size={14} />
                          Back
                        </Button>
                        <Badge variant="secondary" className="font-normal text-xs">
                          {categories.find((c) => c.id === currentCat)?.name ||
                            "Repairs"}
                        </Badge>
                        {!categoryRepairsLoading && (
                          <span className="text-xs text-muted-foreground">
                            ({categoryRepairs.length} predefined repair{categoryRepairs.length !== 1 ? "s" : ""})
                          </span>
                        )}
                      </div>
                    </div>

                    <ScrollArea className="flex-1 pr-2">
                      {categoryRepairsLoading ? (
                        <RepairCardSkeleton />
                      ) : categoryRepairs.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                          <Wrench size={40} className="mb-3 opacity-20" />
                          <p className="text-sm">
                            No predefined repairs for this category.
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-4 border-orange-500/50 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950/20"
                            onClick={handleFromScratch}
                          >
                            <Plus size={14} className="mr-1" />
                            Create from Scratch
                          </Button>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-3 pb-4">
                          {categoryRepairs.slice(0, 25).map((repair) => (
                            <PredefinedRepairCard
                              key={repair.id}
                              repair={repair}
                              isSelected={selectedRepair?.id === repair.id}
                              onSelect={handleSelectRepair}
                              currencySymbol={currencySymbol}
                            />
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </div>
                )}
              </TabsContent>

              {/* -- All Repairs -- */}
              <TabsContent
                value="all-repairs"
                className="h-full m-0 data-[state=active]:flex flex-col"
              >
                <div className="flex flex-col h-full overflow-hidden">
                  <ScrollArea className="flex-1 pr-2">
                    {repairsLoading ? (
                      <RepairCardSkeleton count={12} />
                    ) : predefinedRepairs.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                        <Search size={40} className="mb-3 opacity-20" />
                        <p className="text-sm">No predefined repairs found.</p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-4 border-orange-500/50 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950/20"
                          onClick={handleFromScratch}
                        >
                          <Plus size={14} className="mr-1" />
                          Create from Scratch
                        </Button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-3 pb-4">
                        {predefinedRepairs.slice(0, 25).map((repair) => (
                          <PredefinedRepairCard
                            key={repair.id}
                            repair={repair}
                            isSelected={selectedRepair?.id === repair.id}
                            onSelect={handleSelectRepair}
                            currencySymbol={currencySymbol}
                          />
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </div>
              </TabsContent>

            </div>
          </Tabs>
        )}

        {/* Action Button: Right Side Bottom with 1 card height gap */}
        <div className="flex-shrink-0 mt-20">
          <div className="p-2 sm:p-2.5 bg-card border border-border rounded-xl shadow-sm">
            {renderActionButtons()}
          </div>
        </div>
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Root render
  // -------------------------------------------------------------------------
  return (
    <>
      <div className="flex h-screen w-full overflow-hidden bg-background">
        {/* Main 1:1 Workspace */}
        <div className="flex-1 min-w-0 flex h-full overflow-hidden">
          {/* Left Panel */}
          <div
            className={`${mobileView === "form" ? "flex" : "hidden"
              } lg:flex w-full lg:w-1/2 flex-1 min-w-0 h-full border-r border-border bg-background shadow-sm z-10 flex-col`}
          >
            {renderLeftPanel()}
          </div>

          {/* Right Panel */}
          <div
            className={`${mobileView === "catalog" ? "flex" : "hidden"
              } lg:flex w-full lg:w-1/2 flex-1 min-w-0 h-full overflow-hidden flex-col bg-background`}
          >
            {renderRightPanel()}
          </div>
        </div>

        {/* Rightmost Panel: Actions Sidebar */}
        <PosActionsSidebar
          mode="repair"
          customer={customer}
          currencySymbol={currencySymbol}
          onSelectRepair={handleSelectExistingRepair}
        />
      </div>

      {/* Add Customer Modal */}
      <CustomerModal
        open={customerModalOpen}
        onOpenChange={setCustomerModalOpen}
        onSuccess={(createdCustomer) => {
          setSelectedCustomerId(createdCustomer.id);
          setCustomer(createdCustomer);
        }}
      />
      {/* Inventory Selector Modal (batch / serial) */}
      <InventorySelectorModal
        show={inventorySelectorOpen}
        onHide={() => {
          setInventorySelectorOpen(false);
          setInventorySelectorTarget(null);
        }}
        type={inventorySelectorTarget?.type || "standard"}
        availableItems={
          inventorySelectorTarget?.type === "serial"
            ? availableSerials.filter((s) => {
              const usedSerials = selectedParts.reduce((acc, part, idx) => {
                if (
                  idx !== inventorySelectorTarget?.partIndex &&
                  part.product_id === inventorySelectorTarget?.productId &&
                  part.serial_numbers
                ) {
                  acc.push(
                    ...part.serial_numbers.map((sn) =>
                      typeof sn === "string" ? sn : sn.serial_number,
                    ),
                  );
                }
                return acc;
              }, []);
              return !usedSerials.includes(
                typeof s === "string" ? s : s.serial_number,
              );
            })
            : availableBatches
        }
        selectedItems={
          inventorySelectorTarget?.type === "serial"
            ? selectedParts[inventorySelectorTarget?.partIndex]
              ?.serial_numbers || []
            : selectedParts[inventorySelectorTarget?.partIndex]
              ?.inventory_batch_id
              ? [
                selectedParts[inventorySelectorTarget?.partIndex]
                  ?.inventory_batch_id,
              ]
              : []
        }
        onSelect={(val) => {
          const idx = inventorySelectorTarget?.partIndex;
          if (idx == null) return;
          const np = [...selectedParts];
          if (inventorySelectorTarget.type === "serial") {
            np[idx] = { ...np[idx], serial_numbers: Array.isArray(val) ? val : [val] };
          } else {
            const batch = availableBatches.find(
              (b) => String(b.id) === String(val),
            );
            np[idx] = {
              ...np[idx],
              inventory_batch_id: val,
              inventory_batch_number: batch?.batch_number || "",
            };
          }
          setSelectedParts(np);
          setInventorySelectorOpen(false);
          setInventorySelectorTarget(null);
        }}
        quantity={
          selectedParts[inventorySelectorTarget?.partIndex]?.quantity_used || 1
        }
      />

      {/* Special Part Modal */}
      <Dialog
        open={specialPartModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setSpecialPartModalOpen(false);
            setNewSpecialPart({
              item_name: "",
              brand_id: "",
              model_id: "",
              required_quantity: 1,
              unit_cost: "",
              retail_price: "",
              supplier_id: "",
              order_link: "",
              tracking_id: "",
              order_notes: "",
              order_date: "",
              status: "pending",
              is_tax_exclusive: false,
              has_purchase_order: false,
              purchase_order_id: "",
            });
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Special / Non-Stock Part</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2 space-y-1">
              <Label className="text-xs">
                Item Name <span className="text-destructive">*</span>
              </Label>
              <Input
                placeholder="Part name"
                value={newSpecialPart.item_name}
                onChange={(e) =>
                  setNewSpecialPart((p) => ({
                    ...p,
                    item_name: e.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">
                Brand <span className="text-destructive">*</span>
              </Label>
              <ReactSelect
                className="basic-single text-xs w-full"
                classNamePrefix="select"
                onInputChange={(value) => setBrandSearchInput(value)}
                options={brands.map((b) => ({
                  value: String(b.id),
                  label: b.name,
                }))}
                value={
                  newSpecialPart.brand_id
                    ? {
                      value: String(newSpecialPart.brand_id),
                      label:
                        brandLabels[String(newSpecialPart.brand_id)] ||
                        brands.find(
                          (b) =>
                            String(b.id) === String(newSpecialPart.brand_id),
                        )?.name ||
                        "Select Brand",
                    }
                    : null
                }
                onChange={(option) =>
                  setNewSpecialPart((p) => ({
                    ...p,
                    brand_id: option ? option.value : "",
                    model_id: "",
                  }))
                }
                placeholder="Search Brand"
                isLoading={isBrandsFetching}
                isClearable
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">
                Model <span className="text-destructive">*</span>
              </Label>
              <ReactSelect
                className="basic-single text-xs w-full"
                classNamePrefix="select"
                onInputChange={(value) => setModelSearchInput(value)}
                options={brandModels.map((m) => ({
                  value: String(m.id),
                  label: m.name,
                }))}
                value={
                  newSpecialPart.model_id
                    ? {
                      value: String(newSpecialPart.model_id),
                      label:
                        modelLabels[String(newSpecialPart.model_id)] ||
                        brandModels.find(
                          (m) =>
                            String(m.id) === String(newSpecialPart.model_id),
                        )?.name ||
                        "Select Model",
                    }
                    : null
                }
                onChange={(option) =>
                  setNewSpecialPart((p) => ({
                    ...p,
                    model_id: option ? option.value : "",
                  }))
                }
                placeholder={
                  newSpecialPart.brand_id ? "Search Model" : "Pick Brand first"
                }
                isDisabled={!newSpecialPart.brand_id}
                isLoading={isModelsFetching}
                isClearable
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">
                Supplier <span className="text-destructive">*</span>
              </Label>
              <ReactSelect
                className="basic-single text-xs w-full"
                classNamePrefix="select"
                onInputChange={(value) => setSupplierSearchInput(value)}
                options={suppliers.map((s) => ({
                  value: String(s.id),
                  label: s.name,
                }))}
                value={
                  newSpecialPart.supplier_id
                    ? {
                      value: String(newSpecialPart.supplier_id),
                      label:
                        supplierLabels[String(newSpecialPart.supplier_id)] ||
                        suppliers.find(
                          (s) =>
                            String(s.id) ===
                            String(newSpecialPart.supplier_id),
                        )?.name ||
                        "Select Supplier",
                    }
                    : null
                }
                onChange={(option) =>
                  setNewSpecialPart((p) => ({
                    ...p,
                    supplier_id: option ? option.value : "",
                  }))
                }
                placeholder="Search Supplier"
                isLoading={isSuppliersFetching}
                isClearable
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">
                Qty <span className="text-destructive">*</span>
              </Label>
              <Input
                type="number"
                min="1"
                value={newSpecialPart.required_quantity}
                onChange={(e) =>
                  setNewSpecialPart((p) => ({
                    ...p,
                    required_quantity: e.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">
                Unit Cost <span className="text-destructive">*</span>
              </Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={newSpecialPart.unit_cost}
                onChange={(e) =>
                  setNewSpecialPart((p) => ({
                    ...p,
                    unit_cost: e.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Retail Price</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={newSpecialPart.retail_price}
                onChange={(e) =>
                  setNewSpecialPart((p) => ({
                    ...p,
                    retail_price: e.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <ShadSelect
                value={newSpecialPart.status}
                onValueChange={(v) =>
                  setNewSpecialPart((p) => ({ ...p, status: v }))
                }
              >
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[
                    "pending",
                    "ordered",
                    "shipped",
                    "received",
                    "cancelled",
                  ].map((s) => (
                    <SelectItem key={s} value={s} className="capitalize">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </ShadSelect>
            </div>
            <div className="col-span-2 space-y-1">
              <Label className="text-xs">Order Link</Label>
              <Input
                placeholder="https://..."
                value={newSpecialPart.order_link}
                onChange={(e) =>
                  setNewSpecialPart((p) => ({
                    ...p,
                    order_link: e.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Tracking ID</Label>
              <Input
                placeholder="Tracking number"
                value={newSpecialPart.tracking_id}
                onChange={(e) =>
                  setNewSpecialPart((p) => ({
                    ...p,
                    tracking_id: e.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Order Date</Label>
              <Input
                type="datetime-local"
                value={newSpecialPart.order_date}
                onChange={(e) =>
                  setNewSpecialPart((p) => ({
                    ...p,
                    order_date: e.target.value,
                  }))
                }
              />
            </div>
            <div className="col-span-2 space-y-1">
              <Label className="text-xs">Order Notes</Label>
              <Textarea
                rows={2}
                placeholder="Any order notes..."
                value={newSpecialPart.order_notes}
                onChange={(e) =>
                  setNewSpecialPart((p) => ({
                    ...p,
                    order_notes: e.target.value,
                  }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSpecialPartModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const {
                  item_name,
                  brand_id,
                  model_id,
                  required_quantity,
                  unit_cost,
                  supplier_id,
                } = newSpecialPart;
                if (
                  !item_name ||
                  !brand_id ||
                  !model_id ||
                  !required_quantity ||
                  !unit_cost ||
                  !supplier_id
                ) {
                  toast.error("Please fill in all required fields");
                  return;
                }
                setSpecialParts((prev) => [...prev, { ...newSpecialPart }]);
                setSpecialPartModalOpen(false);
                setNewSpecialPart({
                  item_name: "",
                  brand_id: "",
                  model_id: "",
                  required_quantity: 1,
                  unit_cost: "",
                  retail_price: "",
                  supplier_id: "",
                  order_link: "",
                  tracking_id: "",
                  order_notes: "",
                  order_date: "",
                  status: "pending",
                  is_tax_exclusive: false,
                  has_purchase_order: false,
                  purchase_order_id: "",
                });
              }}
            >
              Add Part
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Details Modal */}
      <RepairDetailsModal
        show={detailsModalOpen}
        onHide={() => {
          setDetailsModalOpen(false);
          setDetailsRepair(null);
        }}
        viewRepairData={detailsRepair}
        currencySymbol={currencySymbol}
        handleEdit={(repair) =>
          navigate(all_routes.editRepair.replace(":id", repair.id))
        }
        onPrintPDF={(repair, type) => {
          handleGeneratePDFReport(repair.id, type);
          setPdfRepairData(repair);
          setPdfModalOpen(true);
        }}
        onRecordPayment={(repair) => {
          setDetailsModalOpen(false);
          setPaymentRepair(repair);
          setPaymentModalOpen(true);
        }}
        hasPermission={hasPermission}
      />

      {/* Record Repair Payment Modal */}
      <RecordRepairPaymentModal
        key={paymentRepair?.id || "repair-payment-modal"}
        show={paymentModalOpen}
        onHide={() => {
          setPaymentModalOpen(false);
          setPaymentRepair(null);
        }}
        repairJob={paymentRepair}
        currencySymbol={currencySymbol}
        hasPermission={hasPermission}
        onSuccess={async () => {
          queryClient.invalidateQueries({ queryKey: ["repair-jobs"] });
          refetchRegularRepairs();
          if (paymentRepair?.id) {
            await refreshSelectedExistingRepair(paymentRepair.id);
          }
        }}
      />

      {/* PDF Modal */}
      <PDFModal
        show={pdfModalOpen}
        onHide={handleClosePDFModal}
        currentRepairData={pdfRepairData}
        isGeneratingPDF={pdfGenerating}
        handleGeneratePDFReport={handleGeneratePDFReport}
        pdfBlobUrl={pdfBlobUrl}
        onClearPreview={() => {
          if (pdfBlobUrl) {
            URL.revokeObjectURL(pdfBlobUrl);
            setPdfBlobUrl(null);
          }
        }}
        currencySymbol={currencySymbol}
      />

      {/* Add Parts Modal */}
      <AddPartsModal
        show={addPartsOpen}
        onHide={() => {
          setAddPartsOpen(false);
          setAddPartsRepair(null);
          setPartsFormData({
            product_id: "",
            quantity_used: 1,
            unit_cost: 0,
            notes: "",
            serial_numbers: [],
            inventory_batch_id: null,
          });
        }}
        selectedRepairJob={addPartsRepair}
        partsFormData={partsFormData}
        setPartsFormData={setPartsFormData}
        handleProductSelect={handleProductSelectForParts}
        isProductsLoading={isAddPartsProductsLoading}
        products={addPartsProducts}
        currencySymbol={currencySymbol}
        handleSerialNumbersChange={handleSerialNumbersChangeForParts}
        handleSaveParts={handleSavePartsModal}
        addPartsMutation={addPartsMutation}
        handleViewParts={handleOpenViewParts}
        repairJobs={combinedRepairJobs}
      />

      {/* Parts View Modal */}
      <PartsViewModal
        show={partsViewOpen}
        onHide={() => {
          setPartsViewOpen(false);
          setPartsViewRepair(null);
        }}
        selectedRepairJob={partsViewRepair}
        partsEntries={partsViewRepair?.parts || []}
        currencySymbol={currencySymbol}
        repairJobs={combinedRepairJobs}
        handleAddParts={handleOpenAddParts}
        products={addPartsProducts}
        hasPermission={hasPermission}
      />

      {/* Labor Entry Modal */}
      <LaborEntriesModal
        show={laborModalOpen}
        onHide={() => {
          setLaborModalOpen(false);
          setLaborRepair(null);
          setNewLaborEntry({
            technician_id: "",
            hourly_rate: "",
            start_time: "",
            end_time: "",
            description: "",
          });
        }}
        selectedRepairJob={laborRepair}
        newLaborEntry={newLaborEntry}
        setNewLaborEntry={setNewLaborEntry}
        handleLaborEntryChange={handleLaborEntryChange}
        isTechniciansLoading={false}
        technicians={employees}
        currencySymbol={currencySymbol}
        handleLaborEntrySave={handleLaborEntrySave}
        addLaborMutation={addLaborMutation}
        handleViewLabor={handleOpenViewLabor}
        repairJobs={combinedRepairJobs}
      />

      {/* Labor View Modal */}
      <LaborViewModal
        show={laborViewOpen}
        onHide={() => {
          setLaborViewOpen(false);
          setLaborRepair(null);
        }}
        selectedRepairJob={laborRepair}
        laborEntries={
          laborRepair?.labor_entries || laborRepair?.laborEntries || []
        }
        currencySymbol={currencySymbol}
        repairJobs={combinedRepairJobs}
        handleAddLabor={handleOpenAddLabor}
        hasPermission={hasPermission}
      />

      {/* Status History Modal */}
      <StatusHistoryModal
        show={statusHistoryOpen}
        onHide={() => {
          setStatusHistoryOpen(false);
          setStatusHistoryRepair(null);
        }}
        selectedRepairJob={statusHistoryRepair}
        statusEntries={
          statusHistoryRepair?.status_history ||
          statusHistoryRepair?.statusHistory ||
          []
        }
        handleStatusUpdate={(repair) => {
          setStatusHistoryOpen(false);
          handleOpenStatusUpdate(repair);
        }}
      />

      {/* Status Update Modal */}
      <StatusUpdateModal
        show={statusUpdateOpen}
        onHide={() => {
          setStatusUpdateOpen(false);
          setStatusUpdateRepair(null);
          setStatusForm({ status: "", notes: "" });
        }}
        selectedRepairJob={statusUpdateRepair}
        statusFormData={statusForm}
        setStatusFormData={setStatusForm}
        handleSaveStatus={() =>
          updateStatusMutation.mutate({
            id: statusUpdateRepair.id,
            status: statusForm.status,
            notes: statusForm.notes,
          })
        }
        updateStatusMutation={updateStatusMutation}
        handleViewStatus={(repair) => {
          setStatusHistoryRepair(repair);
          setStatusHistoryOpen(true);
        }}
        repairJobs={combinedRepairJobs}
      />
    </>
  );
};

export default RepairPOS;
